/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import * as React from 'react';
import React__default, { createContext, useContext, Children, useState, useRef, useMemo, forwardRef, useCallback, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useField } from '@zendeskgarden/container-field';
import styled, { css } from 'styled-components';
import { retrieveComponentStyles, DEFAULT_THEME, getLineHeight, getColor, withTheme, useDocument } from '@zendeskgarden/react-theming';
import { hideVisually, math, rgba, em } from 'polished';
import { composeEventHandlers, KEY_CODES, getControlledValue } from '@zendeskgarden/container-utilities';
import mergeRefs from 'react-merge-refs';
import debounce from 'lodash.debounce';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends$t() {
  _extends$t = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$t.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var FieldContext = createContext(undefined);
var useFieldContext = function useFieldContext() {
  var fieldContext = useContext(FieldContext);
  return fieldContext;
};

var COMPONENT_ID$K = 'forms.field';
var StyledField = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$K,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledField",
  componentId: "sc-12gzfsu-0"
})(["position:relative;direction:", ";margin:0;border:0;padding:0;font-size:0;", ";"], function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$K, props);
});
StyledField.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$J = 'forms.fieldset';
var StyledFieldset = styled(StyledField).attrs({
  as: 'fieldset',
  'data-garden-id': COMPONENT_ID$J,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFieldset",
  componentId: "sc-1vr4mxv-0"
})(["", "{margin-top:", "px;}", ";"], StyledField, function (props) {
  return props.theme.space.base * (props.isCompact ? 1 : 2);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$J, props);
});
StyledFieldset.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$I = 'forms.input_label';
var StyledLabel = styled.label.attrs({
  'data-garden-id': COMPONENT_ID$I,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledLabel",
  componentId: "sc-2utmsz-0"
})(["direction:", ";vertical-align:middle;line-height:", ";color:", ";font-size:", ";font-weight:", ";&[hidden]{display:", ";vertical-align:", ";text-indent:", ";font-size:", ";", ";}", ";"], function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.theme.colors.foreground;
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return props.isRegular ? props.theme.fontWeights.regular : props.theme.fontWeights.semibold;
}, function (props) {
  return props.isRadio ? 'inline-block' : 'inline';
}, function (props) {
  return props.isRadio && 'top';
}, function (props) {
  return props.isRadio && '-100%';
}, function (props) {
  return props.isRadio && '0';
}, function (props) {
  return !props.isRadio && hideVisually();
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$I, props);
});
StyledLabel.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$H = 'forms.fieldset_legend';
var StyledLegend = styled(StyledLabel).attrs({
  as: 'legend',
  'data-garden-id': COMPONENT_ID$H,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledLegend",
  componentId: "sc-6s0zwq-0"
})(["padding:0;", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$H, props);
});
StyledLegend.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$G = 'forms.input_hint';
var StyledHint = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$G,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledHint",
  componentId: "sc-17c2wu8-0"
})(["direction:", ";display:block;vertical-align:middle;line-height:", ";color:", ";font-size:", ";", ";"], function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return getColor('neutralHue', 600, props.theme);
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$G, props);
});
StyledHint.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$s() { _extends$s = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$s.apply(this, arguments); }

var _ref$s = /*#__PURE__*/React.createElement("g", {
  fill: "none",
  stroke: "currentColor"
}, /*#__PURE__*/React.createElement("circle", {
  cx: 7.5,
  cy: 8.5,
  r: 7
}), /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  d: "M7.5 4.5V9"
}));

var _ref2$5 = /*#__PURE__*/React.createElement("circle", {
  cx: 7.5,
  cy: 12,
  r: 1,
  fill: "currentColor"
});

function SvgAlertErrorStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$s, _ref2$5);
}

function _extends$r() { _extends$r = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$r.apply(this, arguments); }

var _ref$r = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M.88 13.77L7.06 1.86c.19-.36.7-.36.89 0l6.18 11.91c.17.33-.07.73-.44.73H1.32c-.37 0-.61-.4-.44-.73zM7.5 6v3.5"
});

var _ref2$4 = /*#__PURE__*/React.createElement("circle", {
  cx: 7.5,
  cy: 12,
  r: 1,
  fill: "currentColor"
});

function SvgAlertWarningStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$r, _ref2$4);
}

function _extends$q() { _extends$q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$q.apply(this, arguments); }

var _ref$q = /*#__PURE__*/React.createElement("g", {
  fill: "none",
  stroke: "currentColor"
}, /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  d: "M4 9l2.5 2.5 5-5"
}), /*#__PURE__*/React.createElement("circle", {
  cx: 7.5,
  cy: 8.5,
  r: 7
}));

function SvgCheckCircleStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$q);
}

var _excluded$i = ["children", "validation"];
var MessageIcon = function MessageIcon(_ref) {
  var children = _ref.children,
      validation = _ref.validation,
      props = _objectWithoutProperties(_ref, _excluded$i);
  var retVal;
  if (validation === 'error') {
    retVal = React__default.createElement(SvgAlertErrorStroke, props);
  } else if (validation === 'success') {
    retVal = React__default.createElement(SvgCheckCircleStroke$1, props);
  } else if (validation === 'warning') {
    retVal = React__default.createElement(SvgAlertWarningStroke, props);
  } else {
    retVal = React__default.cloneElement(Children.only(children));
  }
  return retVal;
};
var COMPONENT_ID$F = 'forms.input_message_icon';
var StyledMessageIcon = styled(MessageIcon).attrs({
  'data-garden-id': COMPONENT_ID$F,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledMessageIcon",
  componentId: "sc-1ph2gba-0"
})(["width:", ";height:", ";", ";"], function (props) {
  return props.theme.iconSizes.md;
}, function (props) {
  return props.theme.iconSizes.md;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$F, props);
});
StyledMessageIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var validationStyles = function validationStyles(props) {
  var rtl = props.theme.rtl;
  var padding = math("".concat(props.theme.space.base, " * 2px + ").concat(props.theme.iconSizes.md));
  var color;
  if (props.validation === 'error') {
    color = getColor('dangerHue', 600, props.theme);
  } else if (props.validation === 'success') {
    color = getColor('successHue', 600, props.theme);
  } else if (props.validation === 'warning') {
    color = getColor('warningHue', 700, props.theme);
  } else {
    color = getColor('neutralHue', 700, props.theme);
  }
  return css(["padding-", ":", ";color:", ";"], rtl ? 'right' : 'left', props.validation && padding, color);
};
var COMPONENT_ID$E = 'forms.input_message';
var StyledMessage = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$E,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledMessage",
  componentId: "sc-30hgg7-0"
})(["direction:", ";display:inline-block;position:relative;vertical-align:middle;line-height:", ";font-size:", ";", ";& ", "{position:absolute;top:-1px;", ":0;}", ":not([hidden]) + &{display:block;margin-top:", ";}", ";"], function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return getLineHeight(props.theme.iconSizes.md, props.theme.fontSizes.sm);
}, function (props) {
  return props.theme.fontSizes.sm;
}, function (props) {
  return validationStyles(props);
}, StyledMessageIcon, function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, StyledLabel, function (props) {
  return math("".concat(props.theme.space.base, " * 1px"));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$E, props);
});
StyledMessage.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$D = 'forms.input';
var isInvalid = function isInvalid(validation) {
  return validation === 'warning' || validation === 'error';
};
var colorStyles$b = function colorStyles(props) {
  var SHADE = 600;
  var placeholderColor = getColor('neutralHue', SHADE - 200, props.theme);
  var borderColor;
  var hoverBorderColor;
  var focusBorderColor;
  if (props.validation) {
    var hue;
    if (props.validation === 'success') {
      hue = 'successHue';
    } else if (props.validation === 'warning') {
      hue = 'warningHue';
    } else if (props.validation === 'error') {
      hue = 'dangerHue';
    }
    borderColor = getColor(hue, SHADE, props.theme);
    hoverBorderColor = borderColor;
    focusBorderColor = borderColor;
  } else {
    borderColor = getColor('neutralHue', SHADE - 300, props.theme);
    hoverBorderColor = getColor('primaryHue', SHADE, props.theme);
    focusBorderColor = hoverBorderColor;
  }
  var boxShadow = "\n    ".concat(props.focusInset ? 'inset' : '', "\n    ").concat(props.theme.shadows.md(rgba(focusBorderColor, 0.35)));
  var readOnlyBackgroundColor = getColor('neutralHue', SHADE - 500, props.theme);
  var readOnlyBorderColor = getColor('neutralHue', SHADE - 300, props.theme);
  var disabledBackgroundColor = readOnlyBackgroundColor;
  var disabledBorderColor = getColor('neutralHue', SHADE - 400, props.theme);
  var disabledForegroundColor = getColor('neutralHue', SHADE - 200, props.theme);
  var controlledBorderColor = borderColor;
  if (props.isFocused) {
    controlledBorderColor = focusBorderColor;
  }
  if (props.isHovered) {
    controlledBorderColor = hoverBorderColor;
  }
  return css(["border-color:", ";box-shadow:", ";background-color:", ";color:", ";&::placeholder{color:", ";}&[readonly],&[aria-readonly='true']{border-color:", ";background-color:", ";}&:hover{border-color:", ";}&:focus,&[data-garden-focus-visible='true']{border-color:", ";box-shadow:", ";}&:disabled,&[aria-disabled='true']{border-color:", ";background-color:", ";color:", ";}"], controlledBorderColor, !props.isBare && props.isFocused && boxShadow, props.isBare ? 'transparent' : props.theme.colors.background, props.theme.colors.foreground, placeholderColor, readOnlyBorderColor, !props.isBare && readOnlyBackgroundColor, hoverBorderColor, focusBorderColor, !props.isBare && boxShadow, disabledBorderColor, !props.isBare && disabledBackgroundColor, disabledForegroundColor);
};
var sizeStyles$f = function sizeStyles(props) {
  var fontSize = props.theme.fontSizes.md;
  var paddingHorizontal = "".concat(props.theme.space.base * 3, "px");
  var height;
  var paddingVertical;
  var browseFontSize;
  var swatchHeight;
  if (props.isCompact) {
    height = "".concat(props.theme.space.base * 8, "px");
    paddingVertical = "".concat(props.theme.space.base * 1.5, "px");
    browseFontSize = math("".concat(props.theme.fontSizes.sm, " - 1"));
    swatchHeight = "".concat(props.theme.space.base * 6, "px");
  } else {
    height = "".concat(props.theme.space.base * 10, "px");
    paddingVertical = "".concat(props.theme.space.base * 2.5, "px");
    browseFontSize = props.theme.fontSizes.sm;
    swatchHeight = "".concat(props.theme.space.base * 7, "px");
  }
  var lineHeight = math("".concat(height, " - (").concat(paddingVertical, " * 2) - (").concat(props.theme.borderWidths.sm, " * 2)"));
  var padding = props.isBare ? '0' : "".concat(em(paddingVertical, fontSize), " ").concat(em(paddingHorizontal, fontSize));
  var swatchMarginVertical = math("(".concat(lineHeight, " - ").concat(swatchHeight, ") / 2"));
  var swatchMarginHorizontal = math("".concat(paddingVertical, " + ").concat(swatchMarginVertical, " - ").concat(paddingHorizontal));
  return css(["padding:", ";min-height:", ";line-height:", ";font-size:", ";&::-ms-browse{font-size:", ";}&[type='date'],&[type='datetime-local'],&[type='file'],&[type='month'],&[type='time'],&[type='week']{max-height:", ";}&[type='file']{line-height:1;}@supports (-ms-ime-align:auto){&[type='color']{padding:", ";}}&::-moz-color-swatch{margin-top:", ";margin-left:", ";width:calc(100% + ", ");height:", ";}&::-webkit-color-swatch{margin:", " ", ";}", ":not([hidden]) + &&,", " + &&,", " + &&,&& + ", ",&& + ", "{margin-top:", "px;}"], padding, props.isBare ? '1em' : height, getLineHeight(lineHeight, fontSize), fontSize, browseFontSize, height, props.isCompact ? '0 2px' : '1px 3px', swatchMarginVertical, swatchMarginHorizontal, math("".concat(swatchMarginHorizontal, " * -2")), swatchHeight, swatchMarginVertical, swatchMarginHorizontal, StyledLabel, StyledHint, StyledMessage, StyledHint, StyledMessage, props.theme.space.base * (props.isCompact ? 1 : 2));
};
var StyledTextInput = styled.input.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$D,
    'data-garden-version': '8.46.0',
    'aria-invalid': isInvalid(props.validation)
  };
}).withConfig({
  displayName: "StyledTextInput",
  componentId: "sc-k12n8x-0"
})(["appearance:none;transition:border-color 0.25s ease-in-out,box-shadow 0.1s ease-in-out,background-color 0.25s ease-in-out,color 0.25s ease-in-out,z-index 0.25s ease-in-out;direction:", ";border:", ";border-radius:", ";width:100%;box-sizing:border-box;vertical-align:middle;font-family:inherit;&::-ms-browse{border-radius:", ";}&::-ms-clear,&::-ms-reveal{display:none;}&::-moz-color-swatch{border:none;border-radius:", ";}&::-webkit-color-swatch{border:none;border-radius:", ";}&::-webkit-color-swatch-wrapper{padding:0;}&::-webkit-clear-button,&::-webkit-inner-spin-button,&::-webkit-search-cancel-button,&::-webkit-search-results-button{display:none;}&::-webkit-datetime-edit{direction:", ";line-height:1;}&::placeholder{opacity:1;}&:invalid{box-shadow:none;}&[type='file']::-ms-value{display:none;}@media screen and (min--moz-device-pixel-ratio:0){&[type='number']{appearance:textfield;}}", ";&:focus{outline:none;}", ";&:disabled{cursor:default;}", ";"], function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return props.isBare ? 'none' : props.theme.borders.sm;
}, function (props) {
  return props.isBare ? '0' : props.theme.borderRadii.md;
}, function (props) {
  return props.theme.borderRadii.sm;
}, function (props) {
  return props.theme.borderRadii.sm;
}, function (props) {
  return props.theme.borderRadii.sm;
}, function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return sizeStyles$f(props);
}, function (props) {
  return colorStyles$b(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$D, props);
});
StyledTextInput.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$C = 'forms.textarea';
var hiddenStyles = "\n  visibility: hidden;\n  position: absolute;\n  overflow: hidden;\n  height: 0;\n  top: 0;\n  left: 0;\n  transform: translateZ(0);\n";
var StyledTextarea = styled(StyledTextInput).attrs({
  as: 'textarea',
  'data-garden-id': COMPONENT_ID$C,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledTextarea",
  componentId: "sc-wxschl-0"
})(["resize:", ";overflow:auto;", ";", ";"], function (props) {
  return props.isResizable ? 'vertical' : 'none';
}, function (props) {
  return props.isHidden && hiddenStyles;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$C, props);
});
StyledTextarea.defaultProps = {
  theme: DEFAULT_THEME
};

var _excluded$h = ["children", "position", "isHovered", "isFocused", "isDisabled", "isRotated", "theme"];
var COMPONENT_ID$B = 'forms.media_figure';
var colorStyles$a = function colorStyles(props) {
  var shade = 600;
  if (props.isDisabled) {
    shade = 400;
  } else if (props.isHovered || props.isFocused) {
    shade = 700;
  }
  return css(["color:", ";"], getColor('neutralHue', shade, props.theme));
};
var sizeStyles$e = function sizeStyles(props) {
  var size = props.theme.iconSizes.md;
  var marginFirst = "1px ".concat(props.theme.space.base * 2, "px auto 0");
  var marginLast = "1px 0 auto ".concat(props.theme.space.base * 2, "px");
  var margin;
  if (props.position === 'start') {
    margin = props.theme.rtl ? marginLast : marginFirst;
  } else {
    margin = props.theme.rtl ? marginFirst : marginLast;
  }
  return css(["margin:", ";width:", ";height:", ";"], margin, size, size);
};
var StyledTextMediaFigure = styled(
function (_ref) {
  var children = _ref.children;
      _ref.position;
      _ref.isHovered;
      _ref.isFocused;
      _ref.isDisabled;
      _ref.isRotated;
      _ref.theme;
      var props = _objectWithoutProperties(_ref, _excluded$h);
  return React__default.cloneElement(Children.only(children), props);
}).attrs({
  'data-garden-id': COMPONENT_ID$B,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledTextMediaFigure",
  componentId: "sc-1qepknj-0"
})(["transform:", ";transition:transform 0.25s ease-in-out,color 0.25s ease-in-out;", ";", " ", ";"], function (props) {
  return props.isRotated && "rotate(".concat(props.theme.rtl ? '-' : '+', "180deg)");
}, function (props) {
  return colorStyles$a(props);
}, function (props) {
  return sizeStyles$e(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$B, props);
});
StyledTextMediaFigure.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$A = 'forms.faux_input';
var StyledTextFauxInput = styled(StyledTextInput).attrs(function (props) {
  return {
    as: 'div',
    'aria-readonly': props.isReadOnly,
    'aria-disabled': props.isDisabled,
    'data-garden-id': COMPONENT_ID$A,
    'data-garden-version': '8.46.0'
  };
}).withConfig({
  displayName: "StyledTextFauxInput",
  componentId: "sc-yqw7j9-0"
})(["display:", ";align-items:", ";cursor:", ";overflow:hidden;& > ", "{vertical-align:", ";}& > ", "{flex-shrink:", ";}", ";"], function (props) {
  return props.mediaLayout ? 'inline-flex' : 'inline-block';
}, function (props) {
  return props.mediaLayout && 'baseline';
}, function (props) {
  return props.mediaLayout && !props.isDisabled ? 'text' : 'default';
}, StyledTextInput, function (props) {
  return !props.mediaLayout && 'baseline';
}, StyledTextMediaFigure, function (props) {
  return props.mediaLayout && '0';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$A, props);
});
StyledTextFauxInput.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$z = 'forms.media_input';
var StyledTextMediaInput = styled(StyledTextInput).attrs({
  'data-garden-id': COMPONENT_ID$z,
  'data-garden-version': '8.46.0',
  isBare: true
}).withConfig({
  displayName: "StyledTextMediaInput",
  componentId: "sc-12i9xfi-0"
})(["flex-grow:1;", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$z, props);
});
StyledTextMediaInput.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$y = 'forms.input_group';
var positionStyles = function positionStyles(props) {
  var topMargin = "".concat(props.theme.space.base * (props.isCompact ? 1 : 2), "px");
  var startDirection = props.theme.rtl ? 'right' : 'left';
  var endDirection = props.theme.rtl ? 'left' : 'right';
  return css(["", ":not([hidden]) + &&,", " + &&,", " + &&,&& + ", ",&& + ", "{margin-top:", ";}& > ", "{position:relative;flex:1 1 auto;margin-top:0;margin-bottom:0;width:auto;min-width:0;}& > ", ":not(:first-child){border-top-", "-radius:0;border-bottom-", "-radius:0;}& > ", ":not(:last-child){border-top-", "-radius:0;border-bottom-", "-radius:0;}"], StyledLabel, StyledHint, StyledMessage, StyledHint, StyledMessage, topMargin, StyledTextInput, StyledTextInput, startDirection, startDirection, StyledTextInput, endDirection, endDirection);
};
var itemStyles = function itemStyles(props) {
  var horizontal = props.theme.rtl ? 'right' : 'left';
  return css(["& > *{margin-", ":-", " !important;z-index:-1;}& > ", ":hover,& > button:hover,& > ", "[data-garden-focus-visible],& > button[data-garden-focus-visible],& > ", ":active,& > button:active{z-index:1;}& > button:disabled{border-top-width:0;border-bottom-width:0;}& > ", ":disabled{z-index:-2;}& > *:first-child:not(:last-child){margin-", ":0;border-top-", "-radius:0;border-bottom-", "-radius:0;}& > *:last-child:not(:first-child){border-top-", "-radius:0;border-bottom-", "-radius:0;}& > *:not(:first-child):not(:last-child){border-radius:0;}"], horizontal, props.theme.borderWidths.sm, StyledTextInput, StyledTextInput, StyledTextInput, StyledTextInput, props.theme.rtl ? 'right' : 'left', props.theme.rtl ? 'left' : 'right', props.theme.rtl ? 'left' : 'right', props.theme.rtl ? 'right' : 'left', props.theme.rtl ? 'right' : 'left');
};
var StyledInputGroup = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$y,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledInputGroup",
  componentId: "sc-kjh1f0-0"
})(["display:inline-flex;position:relative;flex-wrap:nowrap;align-items:stretch;z-index:0;width:100%;", ";", ";", ";"], function (props) {
  return positionStyles(props);
}, function (props) {
  return itemStyles(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$y, props);
});
StyledInputGroup.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$x = 'forms.radio_label';
var sizeStyles$d = function sizeStyles(props) {
  var size = props.theme.space.base * 4;
  var padding = size + props.theme.space.base * 2;
  var lineHeight = props.theme.space.base * 5;
  return css(["padding-", ":", "px;&[hidden]{padding-", ":", "px;line-height:", "px;}"], props.theme.rtl ? 'right' : 'left', padding, props.theme.rtl ? 'right' : 'left', size, lineHeight);
};
var StyledRadioLabel = styled(StyledLabel).attrs({
  'data-garden-id': COMPONENT_ID$x,
  'data-garden-version': '8.46.0',
  isRadio: true
}).withConfig({
  displayName: "StyledRadioLabel",
  componentId: "sc-1aq2e5t-0"
})(["display:inline-block;position:relative;cursor:pointer;user-select:none;", ";", ";"], function (props) {
  return sizeStyles$d(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$x, props);
});
StyledRadioLabel.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$w = 'forms.checkbox_label';
var StyledCheckLabel = styled(StyledRadioLabel).attrs({
  'data-garden-id': COMPONENT_ID$w,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledCheckLabel",
  componentId: "sc-x7nr1-0"
})(["", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$w, props);
});
StyledCheckLabel.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$v = 'forms.radio_hint';
var StyledRadioHint = styled(StyledHint).attrs({
  'data-garden-id': COMPONENT_ID$v,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledRadioHint",
  componentId: "sc-eo8twg-0"
})(["padding-", ":", ";", ";"], function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return math("".concat(props.theme.space.base, " * 6px"));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$v, props);
});
StyledRadioHint.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$u = 'forms.checkbox_hint';
var StyledCheckHint = styled(StyledRadioHint).attrs({
  'data-garden-id': COMPONENT_ID$u,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledCheckHint",
  componentId: "sc-1kl8e8c-0"
})(["", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$u, props);
});
StyledCheckHint.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$t = 'forms.radio';
var colorStyles$9 = function colorStyles(props) {
  var SHADE = 600;
  var borderColor = getColor('neutralHue', SHADE - 300, props.theme);
  var backgroundColor = props.theme.colors.background;
  var iconColor = backgroundColor;
  var hoverBackgroundColor = getColor('primaryHue', SHADE, props.theme, 0.08);
  var hoverBorderColor = getColor('primaryHue', SHADE, props.theme);
  var focusBorderColor = hoverBorderColor;
  var activeBackgroundColor = getColor('primaryHue', SHADE, props.theme, 0.2);
  var activeBorderColor = focusBorderColor;
  var boxShadow = props.theme.shadows.md(rgba(focusBorderColor, 0.35));
  var checkedBorderColor = focusBorderColor;
  var checkedBackgroundColor = checkedBorderColor;
  var checkedHoverBorderColor = getColor('primaryHue', SHADE + 100, props.theme);
  var checkedHoverBackgroundColor = checkedHoverBorderColor;
  var checkedActiveBorderColor = getColor('primaryHue', SHADE + 200, props.theme);
  var checkedActiveBackgroundColor = checkedActiveBorderColor;
  var disabledBackgroundColor = getColor('neutralHue', SHADE - 400, props.theme);
  return css(["& ~ ", "::before{border-color:", ";background-color:", ";}& ~ ", " > svg{color:", ";}& ~ ", ":hover::before{border-color:", ";background-color:", ";}&[data-garden-focus-visible='true'] ~ ", "::before{border-color:", ";box-shadow:", ";}& ~ ", ":active::before{border-color:", ";background-color:", ";}&:checked ~ ", "::before{border-color:", ";background-color:", ";}&:enabled:checked ~ ", ":hover::before{border-color:", ";background-color:", ";}&:enabled:checked ~ ", ":active::before{border-color:", ";background-color:", ";}&:disabled ~ ", "::before{border-color:transparent;background-color:", ";}"], StyledRadioLabel, borderColor, backgroundColor, StyledRadioLabel, iconColor, StyledRadioLabel, hoverBorderColor, hoverBackgroundColor, StyledRadioLabel, focusBorderColor, boxShadow, StyledRadioLabel, activeBorderColor, activeBackgroundColor, StyledRadioLabel, checkedBorderColor, checkedBackgroundColor, StyledRadioLabel, checkedHoverBorderColor, checkedHoverBackgroundColor, StyledRadioLabel, checkedActiveBorderColor, checkedActiveBackgroundColor, StyledRadioLabel, disabledBackgroundColor);
};
var sizeStyles$c = function sizeStyles(props) {
  var lineHeight = "".concat(props.theme.space.base * 5, "px");
  var size = "".concat(props.theme.space.base * 4, "px");
  var top = math("(".concat(lineHeight, " - ").concat(size, ") / 2"));
  var iconSize = props.theme.iconSizes.sm;
  var iconPosition = math("(".concat(size, " - ").concat(iconSize, ") / 2"));
  var iconTop = math("".concat(iconPosition, " + ").concat(top));
  var marginTop = "".concat(props.theme.space.base * (props.isCompact ? 1 : 2), "px");
  return css(["& ~ ", "::before{top:", ";background-size:", ";width:", ";height:", ";box-sizing:border-box;}& ~ ", " > svg{top:", ";", ":", ";width:", ";height:", ";}&& ~ ", " ~ ", "{margin-top:", ";}"], StyledRadioLabel, top, props.theme.iconSizes.sm, size, size, StyledRadioLabel, iconTop, props.theme.rtl ? 'right' : 'left', iconPosition, iconSize, iconSize, StyledRadioLabel, StyledMessage, marginTop);
};
var StyledRadioInput = styled.input.attrs({
  'data-garden-id': COMPONENT_ID$t,
  'data-garden-version': '8.46.0',
  type: 'radio'
}).withConfig({
  displayName: "StyledRadioInput",
  componentId: "sc-qsavpv-0"
})(["position:absolute;clip:rect(1px,1px,1px,1px);& ~ ", "::before{position:absolute;", ":0;transition:border-color .25s ease-in-out,box-shadow .1s ease-in-out,background-color .25s ease-in-out,color .25s ease-in-out;border:", ";border-radius:50%;background-repeat:no-repeat;background-position:center;content:'';}& ~ ", " > svg{position:absolute;}", ";&:focus ~ ", "::before{outline:none;}& ~ ", ":active::before{transition:border-color 0.1s ease-in-out,background-color 0.1s ease-in-out,color 0.1s ease-in-out;}", ";&:disabled ~ ", "{cursor:default;}", ";"], StyledRadioLabel, function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return props.theme.borders.sm;
}, StyledRadioLabel, function (props) {
  return sizeStyles$c(props);
}, StyledRadioLabel, StyledRadioLabel, function (props) {
  return colorStyles$9(props);
}, StyledRadioLabel, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$t, props);
});
StyledRadioInput.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$s = 'forms.checkbox';
var colorStyles$8 = function colorStyles(props) {
  var SHADE = 600;
  var indeterminateBorderColor = getColor('primaryHue', SHADE, props.theme);
  var indeterminateBackgroundColor = indeterminateBorderColor;
  var indeterminateActiveBorderColor = getColor('primaryHue', SHADE + 100, props.theme);
  var indeterminateActiveBackgroundColor = indeterminateActiveBorderColor;
  var indeterminateDisabledBackgroundColor = getColor('neutralHue', SHADE - 400, props.theme);
  return css(["&:indeterminate ~ ", "::before{border-color:", ";background-color:", ";}&:enabled:indeterminate ~ ", ":active::before{border-color:", ";background-color:", ";}&:disabled:indeterminate ~ ", "::before{border-color:transparent;background-color:", ";}"], StyledCheckLabel, indeterminateBorderColor, indeterminateBackgroundColor, StyledCheckLabel, indeterminateActiveBorderColor, indeterminateActiveBackgroundColor, StyledCheckLabel, indeterminateDisabledBackgroundColor);
};
var StyledCheckInput = styled(StyledRadioInput).attrs({
  'data-garden-id': COMPONENT_ID$s,
  'data-garden-version': '8.46.0',
  type: 'checkbox'
}).withConfig({
  displayName: "StyledCheckInput",
  componentId: "sc-176jxxe-0"
})(["& ~ ", "::before{border-radius:", ";}", ";", ";"], StyledCheckLabel, function (props) {
  return props.theme.borderRadii.md;
}, function (props) {
  return colorStyles$8(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$s, props);
});
StyledCheckInput.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$r = 'forms.radio_message';
var StyledRadioMessage = styled(StyledMessage).attrs({
  'data-garden-id': COMPONENT_ID$r,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledRadioMessage",
  componentId: "sc-1pmi0q8-0"
})(["padding-", ":", ";", ";"], function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return math("".concat(props.theme.space.base, " * 6px"));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$r, props);
});
StyledRadioMessage.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$q = 'forms.checkbox_message';
var StyledCheckMessage = styled(StyledRadioMessage).attrs({
  'data-garden-id': COMPONENT_ID$q,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledCheckMessage",
  componentId: "sc-s4p6kd-0"
})(["", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$q, props);
});
StyledCheckMessage.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$p() { _extends$p = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$p.apply(this, arguments); }

var _ref$p = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 2,
  d: "M3 6l2 2 4-4"
});

function SvgCheckSmFill(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$p);
}

var COMPONENT_ID$p = 'forms.check_svg';
var StyledCheckSvg = styled(SvgCheckSmFill).attrs({
  'data-garden-id': COMPONENT_ID$p,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledCheckSvg",
  componentId: "sc-fvxetk-0"
})(["transition:opacity 0.25 ease-in-out;opacity:0;pointer-events:none;", ":checked ~ ", " > &{opacity:1;}", ":indeterminate ~ ", " > &{opacity:0;}", ";"], StyledCheckInput, StyledCheckLabel, StyledCheckInput, StyledCheckLabel, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$p, props);
});
StyledCheckSvg.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$o() { _extends$o = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$o.apply(this, arguments); }

var _ref$o = /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeWidth: 2,
  d: "M3 6h6"
});

function SvgDashFill(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$o);
}

var COMPONENT_ID$o = 'forms.dash_svg';
var StyledDashSvg = styled(SvgDashFill).attrs({
  'data-garden-id': COMPONENT_ID$o,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledDashSvg",
  componentId: "sc-z3vq71-0"
})(["transition:opacity 0.25 ease-in-out;opacity:0;pointer-events:none;", ":indeterminate ~ ", " > &{opacity:1;}", ";"], StyledCheckInput, StyledCheckLabel, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$o, props);
});
StyledDashSvg.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$n = 'forms.file_upload';
var colorStyles$7 = function colorStyles(props) {
  var baseColor = getColor('primaryHue', 600, props.theme);
  var hoverColor = getColor('primaryHue', 700, props.theme);
  var activeColor = getColor('primaryHue', 800, props.theme);
  var disabledBackgroundColor = getColor('neutralHue', 200, props.theme);
  var disabledForegroundColor = getColor('neutralHue', 400, props.theme);
  var boxShadow = "inset ".concat(props.theme.shadows.md(rgba(baseColor, 0.35)));
  return css(["border-color:", ";background-color:", ";color:", ";&:hover{border-color:", ";background-color:", ";color:", ";}&[data-garden-focus-visible]{box-shadow:", ";}&:active{border-color:", ";background-color:", ";color:", ";}&[aria-disabled='true']{border-color:", ";background-color:", ";color:", ";}"], props.isDragging ? activeColor : getColor('neutralHue', 600, props.theme), props.isDragging && rgba(baseColor, 0.2), props.isDragging ? activeColor : baseColor, hoverColor, rgba(baseColor, 0.08), hoverColor, boxShadow, activeColor, rgba(baseColor, 0.2), activeColor, disabledForegroundColor, disabledBackgroundColor, disabledForegroundColor);
};
var sizeStyles$b = function sizeStyles(props) {
  var marginTop = "".concat(props.theme.space.base * (props.isCompact ? 1 : 2), "px");
  var paddingHorizontal = "".concat(props.isCompact ? 2 : 4, "em");
  var paddingVertical = math("".concat(props.theme.space.base * (props.isCompact ? 2.5 : 5), " - ").concat(props.theme.borderWidths.sm));
  var fontSize = props.theme.fontSizes.md;
  var lineHeight = getLineHeight(props.theme.space.base * 5, fontSize);
  return css(["padding:", " ", ";min-width:4em;line-height:", ";font-size:", ";", ":not([hidden]) + &&,", " + &&,", " + &&,&& + ", ",&& + ", "{margin-top:", ";}"], paddingVertical, paddingHorizontal, lineHeight, fontSize, StyledLabel, StyledHint, StyledMessage, StyledHint, StyledMessage, marginTop);
};
var StyledFileUpload = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$n,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFileUpload",
  componentId: "sc-1rodjgn-0"
})(["display:flex;align-items:center;justify-content:center;box-sizing:border-box;direction:", ";transition:border-color 0.25s ease-in-out,box-shadow 0.1s ease-in-out,background-color 0.25s ease-in-out,color 0.25s ease-in-out;border:dashed ", ";border-radius:", ";cursor:pointer;text-align:center;user-select:none;", ";&:focus{outline:none;}&[aria-disabled='true']{cursor:default;}", ";", ";"], function (props) {
  return props.theme.rtl ? 'rtl' : 'ltr';
}, function (props) {
  return props.theme.borderWidths.sm;
}, function (props) {
  return props.theme.borderRadii.md;
}, sizeStyles$b, colorStyles$7, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$n, props);
});
StyledFileUpload.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$m = 'forms.file.close';
var StyledFileClose = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$m,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFileClose",
  componentId: "sc-1m31jbf-0"
})(["display:flex;flex-shrink:0;align-items:center;justify-content:center;transition:opacity 0.25s ease-in-out;opacity:0.8;cursor:pointer;color:", ";&:hover{opacity:0.9;}&:focus{outline:none;}", ";"], function (props) {
  return props.theme.colors.foreground;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$m, props);
});
StyledFileClose.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$l = 'forms.file';
var colorStyles$6 = function colorStyles(props) {
  var borderColor;
  var focusBorderColor;
  var foregroundColor;
  if (props.validation === 'success') {
    borderColor = getColor('successHue', 600, props.theme);
    focusBorderColor = borderColor;
    foregroundColor = borderColor;
  } else if (props.validation === 'error') {
    borderColor = getColor('dangerHue', 600, props.theme);
    focusBorderColor = borderColor;
    foregroundColor = borderColor;
  } else {
    borderColor = getColor('neutralHue', 300, props.theme);
    focusBorderColor = getColor('primaryHue', 600, props.theme);
    foregroundColor = props.theme.colors.foreground;
  }
  var boxShadow = "\n    ".concat(props.focusInset ? 'inset' : '', "\n    ").concat(props.theme.shadows.md(rgba(focusBorderColor, 0.35)));
  return "\n    border-color: ".concat(borderColor, ";\n    color: ").concat(foregroundColor, ";\n\n    &:focus {\n      box-shadow: ").concat(boxShadow, ";\n      border-color: ").concat(focusBorderColor, ";\n    }\n  ");
};
var sizeStyles$a = function sizeStyles(props) {
  var size = "".concat(props.theme.space.base * (props.isCompact ? 7 : 10), "px");
  var spacing = "".concat(props.theme.space.base * (props.isCompact ? 2 : 3), "px");
  var fontSize = props.theme.fontSizes.md;
  var lineHeight = getLineHeight(props.theme.space.base * 5, fontSize);
  return "\n    box-sizing: border-box;\n    border: ".concat(props.theme.borders.sm, ";\n    border-radius: ").concat(props.theme.borderRadii.md, ";\n    padding: 0 ").concat(spacing, ";\n    height: ").concat(size, ";\n    line-height: ").concat(lineHeight, ";\n    font-size: ").concat(fontSize, ";\n\n    & > span {\n      width: 100%;\n    }\n\n    & > ").concat(StyledFileClose, " {\n      width: ").concat(size, ";\n      height: ").concat(size, ";\n      margin-").concat(props.theme.rtl ? 'left' : 'right', ": -").concat(spacing, ";\n    }\n  ");
};
var StyledFile = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$l,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFile",
  componentId: "sc-195lzp1-0"
})(["display:flex;position:relative;flex-wrap:nowrap;align-items:center;", ";&:focus{outline:none;}", ";& > span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}& > [role='progressbar']{position:absolute;bottom:0;left:0;transition:opacity 0.2s ease-in-out;margin:0;border-top-left-radius:0;border-top-right-radius:0;width:100%;& > div{border-top-", "-radius:0;}}& > [role='progressbar'][aria-valuenow='0'],& > [role='progressbar'][aria-valuenow='100']{opacity:0;}", ";"], sizeStyles$a, colorStyles$6, function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$l, props);
});
StyledFile.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$k = 'forms.file.delete';
var StyledFileDelete = styled(StyledFileClose).attrs({
  'data-garden-id': COMPONENT_ID$k,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFileDelete",
  componentId: "sc-a8nnhx-0"
})(["color:", ";", ";"], function (props) {
  return getColor('dangerHue', 600, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$k, props);
});
StyledFileDelete.defaultProps = {
  theme: DEFAULT_THEME
};

var _excluded$g = ["children", "isCompact", "theme"];
var COMPONENT_ID$j = 'forms.file.icon';
var StyledFileIcon = styled(function (_ref) {
  var children = _ref.children;
      _ref.isCompact;
      _ref.theme;
      var props = _objectWithoutProperties(_ref, _excluded$g);
  return React__default.cloneElement(Children.only(children), props);
}).attrs({
  'data-garden-id': COMPONENT_ID$j,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFileIcon",
  componentId: "sc-7b3q0c-0"
})(["flex-shrink:0;width:", ";margin-", ":", "px;", ";"], function (props) {
  return props.isCompact ? props.theme.iconSizes.sm : props.theme.iconSizes.md;
}, function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return props.theme.space.base * 2;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$j, props);
});
StyledFileIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$i = 'forms.file_list';
var StyledFileList = styled.ul.attrs({
  'data-garden-id': COMPONENT_ID$i,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFileList",
  componentId: "sc-gbahjg-0"
})(["margin:0;padding:0;list-style:none;", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$i, props);
});
StyledFileList.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$h = 'forms.file_list.item';
var StyledFileListItem = styled.li.attrs({
  'data-garden-id': COMPONENT_ID$h,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFileListItem",
  componentId: "sc-1ova3lo-0"
})(["&:not(:first-child),", " ~ ", " > &:first-child{margin-top:", "px;}", ";"], StyledFileUpload, StyledFileList, function (props) {
  return props.theme.space.base * 2;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$h, props);
});
StyledFileListItem.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$n() { _extends$n = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$n.apply(this, arguments); }

var _ref$n = /*#__PURE__*/React.createElement("circle", {
  cx: 6,
  cy: 6,
  r: 2,
  fill: "currentColor"
});

function SvgCircleSmFill$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$n);
}

var COMPONENT_ID$g = 'forms.radio_svg';
var StyledRadioSvg = styled(SvgCircleSmFill$1).attrs({
  'data-garden-id': COMPONENT_ID$g,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledRadioSvg",
  componentId: "sc-1r1qtr1-0"
})(["transition:opacity 0.25 ease-in-out;opacity:0;", ":checked ~ ", " > &{opacity:1;}", ";"], StyledRadioInput, StyledRadioLabel, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$g, props);
});
StyledRadioSvg.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$f = 'forms.toggle_label';
var sizeStyles$9 = function sizeStyles(props) {
  var size = props.theme.space.base * 10;
  var padding = size + props.theme.space.base * 2;
  return css(["padding-", ":", "px;&[hidden]{padding-", ":", "px;}"], props.theme.rtl ? 'right' : 'left', padding, props.theme.rtl ? 'right' : 'left', size);
};
var StyledToggleLabel = styled(StyledCheckLabel).attrs({
  'data-garden-id': COMPONENT_ID$f,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledToggleLabel",
  componentId: "sc-e0asdk-0"
})(["", ";", ";"], function (props) {
  return sizeStyles$9(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$f, props);
});
StyledToggleLabel.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$e = 'forms.toggle_hint';
var StyledToggleHint = styled(StyledHint).attrs({
  'data-garden-id': COMPONENT_ID$e,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledToggleHint",
  componentId: "sc-nziggu-0"
})(["padding-", ":", ";", ";"], function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return math("".concat(props.theme.space.base, " * 12px"));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$e, props);
});
StyledToggleHint.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$d = 'forms.toggle';
var colorStyles$5 = function colorStyles(props) {
  var SHADE = 600;
  var backgroundColor = getColor('neutralHue', SHADE - 100, props.theme);
  var hoverBackgroundColor = getColor('neutralHue', SHADE, props.theme);
  var activeBackgroundColor = getColor('neutralHue', SHADE + 100, props.theme);
  return css(["& ~ ", "::before{background-color:", ";}&:enabled ~ ", ":hover::before{background-color:", ";}&:enabled ~ ", ":active::before{background-color:", ";}"], StyledToggleLabel, backgroundColor, StyledToggleLabel, hoverBackgroundColor, StyledToggleLabel, activeBackgroundColor);
};
var sizeStyles$8 = function sizeStyles(props) {
  var height = "".concat(props.theme.space.base * 5, "px");
  var width = "".concat(props.theme.space.base * 10, "px");
  var iconSize = props.theme.iconSizes.md;
  var iconPosition = math("(".concat(height, " - ").concat(iconSize, ") / 2"));
  var checkedIconPosition = math("".concat(width, " - ").concat(iconSize, " - ").concat(iconPosition));
  return css(["& ~ ", "::before{width:", ";height:", ";}& ~ ", " > svg{top:", ";", ":", ";width:", ";height:", ";}&:checked ~ ", " > svg{", ":", ";}"], StyledToggleLabel, width, height, StyledToggleLabel, iconPosition, props.theme.rtl ? 'right' : 'left', iconPosition, iconSize, iconSize, StyledToggleLabel, props.theme.rtl ? 'right' : 'left', checkedIconPosition);
};
var StyledToggleInput = styled(StyledCheckInput).attrs({
  'data-garden-id': COMPONENT_ID$d,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledToggleInput",
  componentId: "sc-sgp47s-0"
})(["& ~ ", "::before{top:0;transition:box-shadow .1s ease-in-out,background-color .15s ease-in-out,color .25s ease-in-out;border:none;border-radius:100px;}", ";", ";", ";"], StyledToggleLabel, function (props) {
  return sizeStyles$8(props);
}, function (props) {
  return colorStyles$5(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$d, props);
});
StyledToggleInput.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$c = 'forms.toggle_message';
var StyledToggleMessage = styled(StyledMessage).attrs({
  'data-garden-id': COMPONENT_ID$c,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledToggleMessage",
  componentId: "sc-13vuvl1-0"
})(["padding-", ":", ";& ", "{", ":", ";}", ";"], function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return math("".concat(props.theme.space.base, " * 12px"));
}, StyledMessageIcon, function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return math("".concat(props.theme.space.base, " * 10px - ").concat(props.theme.iconSizes.md));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$c, props);
});
StyledToggleMessage.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$m() { _extends$m = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$m.apply(this, arguments); }

var _ref$m = /*#__PURE__*/React.createElement("circle", {
  cx: 8,
  cy: 8,
  r: 6,
  fill: "currentColor"
});

function SvgCircleSmFill(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$m);
}

var COMPONENT_ID$b = 'forms.toggle_svg';
var StyledToggleSvg = styled(SvgCircleSmFill).attrs({
  'data-garden-id': COMPONENT_ID$b,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledToggleSvg",
  componentId: "sc-162xbyx-0"
})(["transition:all 0.15s ease-in-out;", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$b, props);
});
StyledToggleSvg.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$a = 'forms.select';
var colorStyles$4 = function colorStyles(props) {
  var color = getColor('neutralHue', 700, props.theme);
  return css(["&:hover + ", ",&:focus + ", ",&[data-garden-focus-visible='true'] + ", "{color:", ";}"], StyledTextMediaFigure, StyledTextMediaFigure, StyledTextMediaFigure, color);
};
var sizeStyles$7 = function sizeStyles(props) {
  var padding = math("".concat(props.theme.iconSizes.md, " + ").concat(props.theme.space.base * 5));
  var iconVerticalPosition = "".concat(props.theme.space.base * (props.isCompact ? 1.5 : 2.5) + 1, "px");
  var iconHorizontalPosition = "".concat(props.theme.space.base * 3, "px");
  return css(["padding-", ":", ";& + ", "{top:", ";", ":", ";}"], props.theme.rtl ? 'left' : 'right', !props.isBare && padding, StyledTextMediaFigure, iconVerticalPosition, props.theme.rtl ? 'left' : 'right', iconHorizontalPosition);
};
var StyledSelect = styled(StyledTextInput).attrs({
  'data-garden-id': COMPONENT_ID$a,
  'data-garden-version': '8.46.0',
  as: 'select'
}).withConfig({
  displayName: "StyledSelect",
  componentId: "sc-8xdxpt-0"
})(["cursor:pointer;text-overflow:ellipsis;", ";", ";&::-ms-expand{display:none;}&::-ms-value{background-color:transparent;color:inherit;}&:-moz-focusring{transition:none;text-shadow:0 0 0 ", ";color:transparent;}& + ", "{position:absolute;pointer-events:none;}"], function (props) {
  return sizeStyles$7(props);
}, function (props) {
  return colorStyles$4(props);
}, function (props) {
  return props.theme.colors.foreground;
}, StyledTextMediaFigure);
StyledSelect.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$9 = 'forms.select_wrapper';
var StyledSelectWrapper = styled(StyledTextFauxInput).attrs({
  'data-garden-id': COMPONENT_ID$9,
  'data-garden-version': '8.46.0',
  isBare: true
}).withConfig({
  displayName: "StyledSelectWrapper",
  componentId: "sc-i7b6hw-0"
})(["position:relative;overflow:visible;"]);
StyledSelectWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$8 = 'forms.range';
var thumbStyles = function thumbStyles(styles) {
  var modifier = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return "\n    &".concat(modifier, "::-moz-range-thumb {\n      ").concat(styles, "\n    }\n\n    &").concat(modifier, "::-ms-thumb {\n      ").concat(styles, "\n    }\n\n    &").concat(modifier, "::-webkit-slider-thumb {\n      ").concat(styles, "\n    }\n  ");
};
var trackStyles = function trackStyles(styles) {
  var modifier = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return "\n    &".concat(modifier, "::-moz-range-track {\n      ").concat(styles, "\n    }\n\n    &").concat(modifier, "::-ms-track {\n      ").concat(styles, "\n    }\n\n    &").concat(modifier, "::-webkit-slider-runnable-track {\n      ").concat(styles, "\n    }\n  ");
};
var trackLowerStyles = function trackLowerStyles(styles) {
  var modifier = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return "\n    &".concat(modifier, "::-moz-range-progress {\n      ").concat(styles, "\n    }\n\n    &").concat(modifier, "::-ms-fill-lower {\n      ").concat(styles, "\n    }\n  ");
};
var colorStyles$3 = function colorStyles(props) {
  var SHADE = 600;
  var thumbBackgroundColor = getColor('primaryHue', SHADE, props.theme);
  var thumbBorderColor = thumbBackgroundColor;
  var thumbBoxShadow = props.theme.shadows.lg(math("".concat(props.theme.space.base, " * 1px")), math("".concat(props.theme.space.base, " * 2px")), getColor('neutralHue', SHADE + 200, props.theme, 0.24));
  var thumbActiveBackgroundColor = getColor('primaryHue', SHADE + 100, props.theme);
  var thumbActiveBorderColor = thumbBorderColor;
  var thumbDisabledBackgroundColor = getColor('neutralHue', SHADE - 300, props.theme);
  var thumbDisabledBorderColor = thumbDisabledBackgroundColor;
  var thumbFocusBoxShadow = props.theme.shadows.md(getColor('primaryHue', SHADE, props.theme, 0.35));
  var thumbHoverBackgroundColor = thumbActiveBackgroundColor;
  var thumbHoverBorderColor = thumbHoverBackgroundColor;
  var trackBackgroundColor = getColor('neutralHue', SHADE - 400, props.theme);
  var trackLowerBackgroundColor = props.hasLowerTrack ? thumbBackgroundColor : '';
  var trackBackgroundImage = props.hasLowerTrack ? "linear-gradient(".concat(trackLowerBackgroundColor, ", ").concat(trackLowerBackgroundColor, ")") : '';
  var trackDisabledLowerBackgroundColor = props.hasLowerTrack ? thumbDisabledBackgroundColor : '';
  var trackDisabledBackgroundImage = props.hasLowerTrack ? "linear-gradient(".concat(trackDisabledLowerBackgroundColor, ", ").concat(trackDisabledLowerBackgroundColor, ")") : '';
  return css(["", " ", " ", " ", " ", " ", " ", " ", " ", ""], trackStyles("\n      background-color: ".concat(trackBackgroundColor, ";\n      background-image: ").concat(trackBackgroundImage, "; /* provide means for styling lower range on WebKit */\n    ")), thumbStyles("\n      border-color: ".concat(thumbBorderColor, ";\n      box-shadow: ").concat(thumbBoxShadow, ";\n      background-color: ").concat(thumbBackgroundColor, ";\n    ")), trackLowerStyles("\n      background-color: ".concat(trackLowerBackgroundColor, ";\n    ")), thumbStyles("\n        transition:\n          border-color .25s ease-in-out,\n          background-color .25s ease-in-out;\n        border-color: ".concat(thumbHoverBorderColor, ";\n        background-color: ").concat(thumbHoverBackgroundColor, ";\n      "), ':hover'), thumbStyles("\n        box-shadow: ".concat(thumbFocusBoxShadow, ";\n      "), '[data-garden-focus-visible="true"]'), thumbStyles("\n        border-color: ".concat(thumbActiveBorderColor, ";\n        background-color: ").concat(thumbActiveBackgroundColor, "\n      "), ':active'), trackStyles("\n        background-image: ".concat(trackDisabledBackgroundImage, ";\n      "), ':disabled'), thumbStyles("\n        border-color: ".concat(thumbDisabledBorderColor, ";\n        box-shadow: none;\n        background-color: ").concat(thumbDisabledBackgroundColor, ";\n      "), ':disabled'), trackLowerStyles("\n        background-color: ".concat(trackDisabledLowerBackgroundColor, "\n      "), ':disabled'));
};
var sizeStyles$6 = function sizeStyles(props) {
  var thumbSize = math("".concat(props.theme.space.base, " * 5px"));
  var trackHeight = math("".concat(props.theme.space.base, " * 1.5px"));
  var trackBorderRadius = trackHeight;
  var trackMargin = math("(".concat(thumbSize, " - ").concat(trackHeight, ") / 2 + ").concat(props.theme.shadowWidths.md));
  var thumbMargin = math("(".concat(trackHeight, " - ").concat(thumbSize, ") / 2"));
  return css(["", ":not([hidden]) + &,", " + &,", " + &,& + ", ",& + ", "{margin-top:", ";}", ";", " ", ""], StyledLabel, StyledHint, StyledMessage, StyledHint, StyledMessage, math("".concat(props.theme.space.base, " * 2px")), trackStyles("\n      margin: ".concat(trackMargin, " 0;\n      border-radius: ").concat(trackBorderRadius, ";\n      height: ").concat(trackHeight, ";\n    ")), thumbStyles("\n      margin: ".concat(thumbMargin, " 0; /* reset for IE */\n      width: ").concat(thumbSize, ";\n      height: ").concat(thumbSize, ";\n    ")), trackLowerStyles("\n      border-top-".concat(props.theme.rtl ? 'right' : 'left', "-radius: ").concat(trackBorderRadius, ";\n      border-bottom-").concat(props.theme.rtl ? 'right' : 'left', "-radius: ").concat(trackBorderRadius, ";\n      height: ").concat(trackHeight, ";\n    ")));
};
var StyledRangeInput = styled.input.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$8,
    'data-garden-version': '8.46.0',
    type: 'range',
    style: {
      backgroundSize: props.hasLowerTrack && props.backgroundSize
    }
  };
}).withConfig({
  displayName: "StyledRangeInput",
  componentId: "sc-1iv2yqp-0"
})(["appearance:none;direction:", ";margin:0;background-color:inherit;cursor:pointer;padding:0;width:100%;vertical-align:middle;", " &::-webkit-slider-container,&::-webkit-slider-runnable-track{background-size:inherit;}", ";", " ", ";&::-moz-focus-outer{border:0;}&::-ms-tooltip{display:none;}&:focus{outline:none;}&:disabled{cursor:default;}", ";"], function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return trackStyles("\n      appearance: none;\n      border-color: transparent; /* reset for IE */\n      background-repeat: repeat-y;\n      background-size: 0;\n      background-position: ".concat(props.theme.rtl ? '100% 100%' : '0% 0%', ";\n      width: 99.8%; /* fix for IE which cuts off the upper track's border radius */\n      color: transparent; /* reset for IE */\n      box-sizing: border-box; /* reset for IE */\n    "));
}, function (props) {
  return sizeStyles$6(props);
}, function (props) {
  return thumbStyles("\n      appearance: none;\n      transition: box-shadow .1s ease-in-out;\n      border: ".concat(props.theme.borders.md, ";\n      border-radius: 100%;\n      box-sizing: border-box;\n    "));
}, function (props) {
  return colorStyles$3(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$8, props);
});
StyledRangeInput.defaultProps = {
  backgroundSize: '0%',
  hasLowerTrack: true,
  theme: DEFAULT_THEME
};

var COMPONENT_ID$7 = 'forms.slider';
var StyledSlider = styled.div.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$7,
    'data-garden-version': '8.46.0',
    'aria-disabled': props.isDisabled
  };
}).withConfig({
  displayName: "StyledSlider",
  componentId: "sc-1di437a-0"
})(["display:block;position:relative;z-index:0;cursor:pointer;height:", ";&[aria-disabled='true']{cursor:default;}", ":not([hidden]) + &,", " + &,", " + &,& + ", ",& + ", "{margin-top:", ";}", ";"], function (props) {
  return math("(".concat(props.theme.space.base, " * 5px) + (").concat(props.theme.shadowWidths.md, " * 2)"));
}, StyledLabel, StyledHint, StyledMessage, StyledHint, StyledMessage, function (props) {
  return math("".concat(props.theme.space.base, " * 2px"));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$7, props);
});
StyledSlider.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$6 = 'forms.slider_thumb';
var colorStyles$2 = function colorStyles(props) {
  var SHADE = 600;
  var backgroundColor = getColor('primaryHue', SHADE, props.theme);
  var borderColor = backgroundColor;
  var boxShadow = props.theme.shadows.lg(math("".concat(props.theme.space.base, " * 1px")), math("".concat(props.theme.space.base, " * 2px")), getColor('neutralHue', SHADE + 200, props.theme, 0.24));
  var activeBackgroundColor = getColor('primaryHue', SHADE + 100, props.theme);
  var activeBorderColor = borderColor;
  var hoverBackgroundColor = activeBackgroundColor;
  var hoverBorderColor = hoverBackgroundColor;
  var focusBoxShadow = props.theme.shadows.md(getColor('primaryHue', SHADE, props.theme, 0.35));
  var disabledBackgroundColor = getColor('neutralHue', SHADE - 300, props.theme);
  var disabledBorderColor = disabledBackgroundColor;
  return css(["border-color:", ";box-shadow:", ";background-color:", ";&[data-garden-focus-visible='true']{box-shadow:", ";}&:hover,&[data-garden-hover='true']{border-color:", ";background-color:", ";}&:active,&[data-garden-active='true']{border-color:", ";background-color:", ";}&[aria-disabled='true']{border-color:", ";box-shadow:none;background-color:", ";}"], borderColor, boxShadow, backgroundColor, focusBoxShadow, hoverBorderColor, hoverBackgroundColor, activeBorderColor, activeBackgroundColor, disabledBorderColor, disabledBackgroundColor);
};
var sizeStyles$5 = function sizeStyles(props) {
  var size = math("".concat(props.theme.space.base, " * 5px"));
  var marginTop = math("".concat(size, " / -2"));
  return css(["margin-top:", ";width:", ";height:", ";"], marginTop, size, size);
};
var StyledSliderThumb = styled.div.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$6,
    'data-garden-version': '8.46.0',
    'aria-disabled': props.isDisabled
  };
}).withConfig({
  displayName: "StyledSliderThumb",
  componentId: "sc-yspbwa-0"
})(["appearance:none;position:absolute;top:50%;", ":", ";transition:border-color 0.25s ease-in-out,box-shadow 0.1s ease-in-out,background-color 0.25s ease-in-out;z-index:1;border:", ";border-radius:100%;cursor:inherit;box-sizing:border-box;font-size:0;", ";&:focus{outline:none;}", ";", ";"], function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return math("".concat(props.position, " * 1px"));
}, function (props) {
  return props.theme.borders.md;
}, function (props) {
  return sizeStyles$5(props);
}, function (props) {
  return colorStyles$2(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$6, props);
});
StyledSliderThumb.defaultProps = {
  position: 0,
  theme: DEFAULT_THEME
};

var COMPONENT_ID$5 = 'forms.slider_track';
var colorStyles$1 = function colorStyles(props) {
  var SHADE = 600;
  var backgroundColor = getColor('neutralHue', SHADE - 400, props.theme);
  var backgroundImageColor = getColor('primaryHue', SHADE, props.theme);
  var disabledBackgroundColor = getColor('neutralHue', SHADE - 300, props.theme);
  return css(["background-color:", ";background-image:linear-gradient(", ",", ");&[aria-disabled='true']{background-image:linear-gradient(", ",", ");}"], backgroundColor, backgroundImageColor, backgroundImageColor, disabledBackgroundColor, disabledBackgroundColor);
};
var sizeStyles$4 = function sizeStyles(props) {
  var height = math("".concat(props.theme.space.base, " * 1.5px"));
  var backgroundPosition = math("".concat(props.backgroundPosition, " * 1px"));
  var backgroundSize = math("".concat(props.backgroundSize, " * 1px"));
  var borderRadius = height;
  var marginTop = math("".concat(height, " / -2"));
  var padding = math("".concat(props.theme.space.base, " * 2.5px"));
  return css(["margin-top:", ";border-radius:", ";background-position:", ";background-size:", ";padding:0 ", ";"], marginTop, borderRadius, backgroundPosition, backgroundSize, padding);
};
var StyledSliderTrack = styled.div.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$5,
    'data-garden-version': '8.46.0',
    'aria-disabled': props.isDisabled
  };
}).withConfig({
  displayName: "StyledSliderTrack",
  componentId: "sc-aw5m6g-0"
})(["position:absolute;top:50%;box-sizing:border-box;background-origin:content-box;background-repeat:repeat-y;width:100%;", ";", ";", ";"], function (props) {
  return sizeStyles$4(props);
}, function (props) {
  return colorStyles$1(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$5, props);
});
StyledSliderTrack.defaultProps = {
  backgroundSize: 0,
  backgroundPosition: 0,
  theme: DEFAULT_THEME
};

var COMPONENT_ID$4 = 'forms.slider_track_rail';
var sizeStyles$3 = function sizeStyles(props) {
  var height = math("".concat(props.theme.space.base, " * 1.5px"));
  var margin = math("".concat(props.theme.space.base, " * 2.5px"));
  return css(["margin:0 ", " 0 ", ";height:", ";"], props.theme.rtl ? "-".concat(margin) : margin, props.theme.rtl ? margin : "-".concat(margin), height);
};
var StyledSliderTrackRail = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$4,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSliderTrackRail",
  componentId: "sc-1o5owbd-0"
})(["position:relative;", ";", ";"], function (props) {
  return sizeStyles$3(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$4, props);
});
StyledSliderTrackRail.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$3 = 'forms.tile_icon';
var sizeStyles$2 = function sizeStyles(props) {
  var iconSize = math("".concat(props.theme.iconSizes.md, " * 2"));
  var position;
  var top;
  var horizontalValue;
  if (!props.isCentered) {
    position = 'absolute';
    top = "".concat(props.theme.space.base * 6, "px");
    horizontalValue = "left: ".concat(props.theme.space.base * 5, "px");
    if (props.theme.rtl) {
      horizontalValue = "right: ".concat(props.theme.space.base * 5, "px");
    }
  }
  return css(["position:", ";top:", ";", ";& > *{width:", ";height:", ";}"], position, top, horizontalValue, iconSize, iconSize);
};
var StyledTileIcon = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$3,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledTileIcon",
  componentId: "sc-1wazhg4-0"
})(["display:block;transition:color 0.25s ease-in-out;text-align:center;line-height:0;", ";", ";"], function (props) {
  return sizeStyles$2(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$3, props);
});
StyledTileIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$2 = 'forms.tile';
var colorStyles = function colorStyles(props) {
  var SHADE = 600;
  var iconColor = getColor('neutralHue', SHADE, props.theme);
  var color = getColor('neutralHue', SHADE + 200, props.theme);
  var borderColor = getColor('neutralHue', SHADE - 300, props.theme);
  var hoverBackgroundColor = getColor('primaryHue', SHADE, props.theme, 0.08);
  var hoverBorderColor = getColor('primaryHue', SHADE, props.theme);
  var focusBorderColor = hoverBorderColor;
  var focusBoxShadow = props.theme.shadows.md(rgba(focusBorderColor, 0.35));
  var activeBackgroundColor = getColor('primaryHue', SHADE, props.theme, 0.2);
  var activeBorderColor = focusBorderColor;
  var disabledBackgroundColor = getColor('neutralHue', SHADE - 500, props.theme);
  var disabledBorderColor = getColor('neutralHue', SHADE - 400, props.theme);
  var disabledColor = getColor('neutralHue', SHADE - 200, props.theme);
  var selectedBorderColor = focusBorderColor;
  var selectedBackgroundColor = selectedBorderColor;
  var selectedHoverBorderColor = getColor('primaryHue', SHADE + 100, props.theme);
  var selectedHoverBackgroundColor = selectedHoverBorderColor;
  var selectedActiveBorderColor = getColor('primaryHue', SHADE + 200, props.theme);
  var selectedActiveBackgroundColor = selectedActiveBorderColor;
  var selectedDisabledBackgroundColor = disabledBorderColor;
  return css(["border:", " ", ";border-color:", ";background-color:", ";color:", ";", "{color:", ";}&:focus{outline:none;}&:hover:not([aria-disabled='true']){border-color:", ";background-color:", ";", "{color:", ";}}&[data-garden-focus-visible='true']{border-color:", ";box-shadow:", ";}&:active:not([aria-disabled='true']){border-color:", ";background-color:", ";", "{color:", ";}}&[data-garden-selected='true']{border-color:", ";background-color:", ";color:", ";", "{color:", ";}}&[data-garden-selected='true']:not([aria-disabled='true']):hover{border-color:", ";background-color:", ";color:", ";", "{color:", ";}}&[data-garden-selected='true']:not([aria-disabled='true']):active{border-color:", ";background-color:", ";color:", ";", "{color:", ";}}&[aria-disabled='true']{border-color:", ";background-color:", ";color:", ";", "{color:", ";}}&[data-garden-selected='true'][aria-disabled='true']{background-color:", ";color:", ";", "{color:", ";}}"], props.theme.borders.sm, getColor('neutralHue', SHADE - 300, props.theme), borderColor, props.theme.colors.background, color, StyledTileIcon, iconColor, hoverBorderColor, hoverBackgroundColor, StyledTileIcon, color, focusBorderColor, focusBoxShadow, activeBorderColor, activeBackgroundColor, StyledTileIcon, color, selectedBorderColor, selectedBackgroundColor, props.theme.colors.background, StyledTileIcon, props.theme.colors.background, selectedHoverBorderColor, selectedHoverBackgroundColor, props.theme.colors.background, StyledTileIcon, props.theme.colors.background, selectedActiveBorderColor, selectedActiveBackgroundColor, props.theme.colors.background, StyledTileIcon, props.theme.colors.background, disabledBorderColor, disabledBackgroundColor, disabledColor, StyledTileIcon, disabledColor, selectedDisabledBackgroundColor, disabledColor, StyledTileIcon, disabledColor);
};
var StyledTile = styled.label.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$2,
    'data-garden-version': '8.46.0',
    'data-garden-focus-visible': props.isFocused,
    'data-garden-selected': props.isSelected
  };
}).withConfig({
  displayName: "StyledTile",
  componentId: "sc-1jjvmxs-0"
})(["display:block;position:relative;transition:border-color .25s ease-in-out,box-shadow .1s ease-in-out,background-color .25s ease-in-out,color .25s ease-in-out;border-radius:", ";cursor:", ";padding:", "px;direction:", ";", ";", ";"], function (props) {
  return props.theme.borderRadii.md;
}, function (props) {
  return !props.isDisabled && 'pointer';
}, function (props) {
  return props.theme.space.base * 5;
}, function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return colorStyles(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$2, props);
});
StyledTile.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$1 = 'forms.tile_description';
var sizeStyles$1 = function sizeStyles(props) {
  var marginDirection = 'left';
  var marginValue;
  if (props.theme.rtl) {
    marginDirection = 'right';
  }
  if (!props.isCentered) {
    marginValue = math("(".concat(props.theme.iconSizes.md, " * 2) + ").concat(props.theme.space.base * 5, "px"));
  }
  return css(["margin-top:", "px;margin-", ":", ";"], props.theme.space.base, marginDirection, marginValue);
};
var StyledTileDescription = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledTileDescription",
  componentId: "sc-xfuu7u-0"
})(["display:block;text-align:", ";line-height:", ";font-size:", ";", ";", ";"], function (props) {
  return props.isCentered && 'center';
}, function (props) {
  return getLineHeight(props.theme.space.base * 4, props.theme.fontSizes.sm);
}, function (props) {
  return props.theme.fontSizes.sm;
}, function (props) {
  return sizeStyles$1(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$1, props);
});
StyledTileDescription.defaultProps = {
  theme: DEFAULT_THEME
};

var StyledTileInput = styled.input.withConfig({
  displayName: "StyledTileInput",
  componentId: "sc-1nq2m6q-0"
})(["position:absolute;border:0;clip:rect(1px,1px,1px,1px);padding:0;width:1px;height:1px;overflow:hidden;white-space:nowrap;"]);
StyledTileInput.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID = 'forms.tile_label';
var sizeStyles = function sizeStyles(props) {
  var marginDirection = 'left';
  var marginTop = "".concat(props.theme.space.base * 2, "px");
  var marginValue;
  if (props.theme.rtl) {
    marginDirection = 'right';
  }
  if (!props.isCentered) {
    marginValue = math("(".concat(props.theme.iconSizes.md, " * 2) + ").concat(props.theme.space.base * 5, "px"));
    marginTop = '0';
  }
  return css(["margin-top:", ";margin-", ":", ";"], marginTop, marginDirection, marginValue);
};
var StyledTileLabel = styled.span.attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledTileLabel",
  componentId: "sc-1mypv27-0"
})(["display:block;text-align:", ";line-height:", ";font-size:", ";font-weight:", ";", ";", ";"], function (props) {
  return props.isCentered && 'center';
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return props.theme.fontWeights.semibold;
}, function (props) {
  return sizeStyles(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID, props);
});
StyledTileLabel.defaultProps = {
  theme: DEFAULT_THEME
};

var Field = React__default.forwardRef(function (props, ref) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isLabelActive = _useState2[0],
      setIsLabelActive = _useState2[1];
  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isLabelHovered = _useState4[0],
      setIsLabelHovered = _useState4[1];
  var multiThumbRangeRef = useRef(null);
  var getMessageProps = function getMessageProps(messageProps) {
    return _objectSpread2({
      role: 'alert'
    }, messageProps);
  };
  var propGetters = useField(props.id);
  var fieldProps = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, propGetters), {}, {
      getMessageProps: getMessageProps,
      isLabelActive: isLabelActive,
      setIsLabelActive: setIsLabelActive,
      isLabelHovered: isLabelHovered,
      setIsLabelHovered: setIsLabelHovered,
      multiThumbRangeRef: multiThumbRangeRef
    });
  }, [propGetters, isLabelActive, isLabelHovered]);
  return React__default.createElement(FieldContext.Provider, {
    value: fieldProps
  }, React__default.createElement(StyledField, _extends$t({}, props, {
    ref: ref
  })));
});
Field.propTypes = {
  id: PropTypes.string
};

var FieldsetContext = createContext(undefined);
var useFieldsetContext = function useFieldsetContext() {
  var fieldsetContext = useContext(FieldsetContext);
  return fieldsetContext;
};

var Legend = forwardRef(function (props, ref) {
  var fieldsetContext = useFieldsetContext();
  return React__default.createElement(StyledLegend, _extends$t({}, props, fieldsetContext, {
    ref: ref
  }));
});
Legend.displayName = 'Legend';

var Fieldset = forwardRef(function (props, ref) {
  var fieldsetContext = useMemo(function () {
    return {
      isCompact: props.isCompact
    };
  }, [props.isCompact]);
  return React__default.createElement(FieldsetContext.Provider, {
    value: fieldsetContext
  }, React__default.createElement(StyledFieldset, _extends$t({}, props, {
    ref: ref
  })));
});
Fieldset.Legend = Legend;
Fieldset.displayName = 'Fieldset';

var InputContext = createContext(undefined);
var useInputContext = function useInputContext() {
  return useContext(InputContext);
};

var Hint = React__default.forwardRef(function (props, ref) {
  var fieldContext = useFieldContext();
  var type = useInputContext();
  var HintComponent;
  if (type === 'checkbox') {
    HintComponent = StyledCheckHint;
  } else if (type === 'radio') {
    HintComponent = StyledRadioHint;
  } else if (type === 'toggle') {
    HintComponent = StyledToggleHint;
  } else {
    HintComponent = StyledHint;
  }
  var combinedProps = props;
  if (fieldContext) {
    combinedProps = fieldContext.getHintProps(combinedProps);
  }
  return React__default.createElement(HintComponent, _extends$t({
    ref: ref
  }, combinedProps));
});
Hint.displayName = 'Hint';

var Label$1 = React__default.forwardRef(function (props, ref) {
  var fieldContext = useFieldContext();
  var fieldsetContext = useFieldsetContext();
  var type = useInputContext();
  var combinedProps = props;
  if (fieldContext) {
    combinedProps = fieldContext.getLabelProps(combinedProps);
    if (type === undefined) {
      var setIsLabelActive = fieldContext.setIsLabelActive,
          setIsLabelHovered = fieldContext.setIsLabelHovered,
          multiThumbRangeRef = fieldContext.multiThumbRangeRef;
      combinedProps = _objectSpread2(_objectSpread2({}, combinedProps), {}, {
        onMouseUp: composeEventHandlers(props.onMouseUp, function () {
          setIsLabelActive(false);
        }),
        onMouseDown: composeEventHandlers(props.onMouseDown, function () {
          setIsLabelActive(true);
        }),
        onMouseEnter: composeEventHandlers(props.onMouseEnter, function () {
          setIsLabelHovered(true);
        }),
        onMouseLeave: composeEventHandlers(props.onMouseLeave, function () {
          setIsLabelHovered(false);
        }),
        onClick: composeEventHandlers(props.onClick, function () {
          multiThumbRangeRef.current && multiThumbRangeRef.current.focus();
        })
      });
    }
  }
  if (fieldsetContext) {
    combinedProps = _objectSpread2(_objectSpread2({}, combinedProps), {}, {
      isRegular: combinedProps.isRegular === undefined ? true : combinedProps.isRegular
    });
  }
  if (type === 'radio') {
    return React__default.createElement(StyledRadioLabel, _extends$t({
      ref: ref
    }, combinedProps), React__default.createElement(StyledRadioSvg, null), props.children);
  } else if (type === 'checkbox') {
    var onLabelSelect = function onLabelSelect(e) {
      var isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      if (fieldContext && isFirefox && e.target instanceof Element) {
        var inputId = e.target.getAttribute('for');
        if (!inputId) return;
        var input = document.getElementById(inputId);
        if (input && input.type === 'checkbox') {
          if (e.shiftKey) {
            input.click();
            input.checked = true;
          }
          input.focus();
        }
      }
    };
    combinedProps = _objectSpread2(_objectSpread2({}, combinedProps), {}, {
      onClick: composeEventHandlers(combinedProps.onClick, onLabelSelect)
    });
    return React__default.createElement(StyledCheckLabel, _extends$t({
      ref: ref
    }, combinedProps), React__default.createElement(StyledCheckSvg, null), React__default.createElement(StyledDashSvg, null), props.children);
  } else if (type === 'toggle') {
    return React__default.createElement(StyledToggleLabel, _extends$t({
      ref: ref
    }, combinedProps), React__default.createElement(StyledToggleSvg, null), props.children);
  }
  return React__default.createElement(StyledLabel, _extends$t({
    ref: ref
  }, combinedProps));
});
Label$1.displayName = 'Label';
Label$1.propTypes = {
  isRegular: PropTypes.bool
};

var _excluded$f = ["validation", "children"];
var Message = React__default.forwardRef(function (_ref, ref) {
  var validation = _ref.validation,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$f);
  var fieldContext = useFieldContext();
  var type = useInputContext();
  var MessageComponent;
  if (type === 'checkbox') {
    MessageComponent = StyledCheckMessage;
  } else if (type === 'radio') {
    MessageComponent = StyledRadioMessage;
  } else if (type === 'toggle') {
    MessageComponent = StyledToggleMessage;
  } else {
    MessageComponent = StyledMessage;
  }
  var combinedProps = _objectSpread2({
    validation: validation
  }, props);
  if (fieldContext) {
    combinedProps = fieldContext.getMessageProps(combinedProps);
  }
  return React__default.createElement(MessageComponent, _extends$t({
    ref: ref
  }, combinedProps), validation && React__default.createElement(StyledMessageIcon, {
    validation: validation
  }), children);
});
Message.displayName = 'Message';
Message.propTypes = {
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var _excluded$e = ["indeterminate", "children"];
var Checkbox = React__default.forwardRef(function (_ref, ref) {
  var indeterminate = _ref.indeterminate,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$e);
  var fieldsetContext = useFieldsetContext();
  var fieldContext = useFieldContext();
  var inputRef = function inputRef(inputElement) {
    inputElement && (inputElement.indeterminate = indeterminate);
  };
  var combinedRef = function combinedRef(inputElement) {
    [inputRef, ref].forEach(function (targetRef) {
      if (targetRef) {
        if (typeof targetRef === 'function') {
          targetRef(inputElement);
        } else {
          targetRef.current = inputElement;
        }
      }
    });
  };
  var combinedProps = _objectSpread2(_objectSpread2({
    ref: combinedRef
  }, props), fieldsetContext);
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps);
  }
  return React__default.createElement(InputContext.Provider, {
    value: "checkbox"
  }, React__default.createElement(StyledCheckInput, combinedProps), children);
});
Checkbox.displayName = 'Checkbox';

var InputGroupContext = createContext(undefined);
var useInputGroupContext = function useInputGroupContext() {
  return useContext(InputGroupContext);
};

var _excluded$d = ["onSelect"];
var Input = React__default.forwardRef(function (_ref, ref) {
  var onSelect = _ref.onSelect,
      props = _objectWithoutProperties(_ref, _excluded$d);
  var fieldContext = useFieldContext();
  var inputGroupContext = useInputGroupContext();
  var onSelectHandler = props.readOnly ? composeEventHandlers(onSelect, function (event) {
    event.currentTarget.select();
  }) : onSelect;
  var combinedProps = _objectSpread2({
    ref: ref,
    onSelect: onSelectHandler
  }, props);
  if (inputGroupContext) {
    combinedProps = _objectSpread2(_objectSpread2({}, combinedProps), {}, {
      isCompact: inputGroupContext.isCompact || combinedProps.isCompact,
      focusInset: props.focusInset === undefined ? true : props.focusInset
    });
  }
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps, {
      isDescribed: true
    });
  }
  return React__default.createElement(StyledTextInput, combinedProps);
});
Input.propTypes = {
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  focusInset: PropTypes.bool,
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var _excluded$c = ["children"];
var Radio = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$c);
  var fieldsetContext = useFieldsetContext();
  var fieldContext = useFieldContext();
  var combinedProps = _objectSpread2(_objectSpread2({
    ref: ref
  }, props), fieldsetContext);
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps);
  }
  return React__default.createElement(InputContext.Provider, {
    value: "radio"
  }, React__default.createElement(StyledRadioInput, combinedProps), children);
});
Radio.displayName = 'Radio';

var _excluded$b = ["hasLowerTrack", "min", "max", "step"];
var Range = React__default.forwardRef(function (_ref, ref) {
  var hasLowerTrack = _ref.hasLowerTrack,
      min = _ref.min,
      max = _ref.max,
      step = _ref.step,
      props = _objectWithoutProperties(_ref, _excluded$b);
  var _useState = useState('0'),
      _useState2 = _slicedToArray(_useState, 2),
      backgroundSize = _useState2[0],
      setBackgroundSize = _useState2[1];
  var rangeRef = useRef();
  var fieldContext = useFieldContext();
  var updateBackgroundWidthFromInput = useCallback(function (rangeTarget) {
    var relativeMax = max;
    var value = rangeTarget.value;
    if (parseFloat(relativeMax) < parseFloat(min)) {
      relativeMax = 100;
    }
    var percentage = 100 * (value - min) / (relativeMax - min);
    setBackgroundSize("".concat(percentage, "%"));
  },
  [max, min, step]);
  useEffect(function () {
    updateBackgroundWidthFromInput(rangeRef.current);
  }, [rangeRef, updateBackgroundWidthFromInput, props.value]);
  var onChange = hasLowerTrack ? composeEventHandlers(props.onChange, function (event) {
    updateBackgroundWidthFromInput(event.target);
  }) : props.onChange;
  var combinedProps = _objectSpread2(_objectSpread2({
    ref: mergeRefs([rangeRef, ref]),
    hasLowerTrack: hasLowerTrack,
    min: min,
    max: max,
    step: step,
    backgroundSize: backgroundSize
  }, props), {}, {
    onChange: onChange
  });
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps, {
      isDescribed: true
    });
  }
  return React__default.createElement(StyledRangeInput, combinedProps);
});
Range.defaultProps = {
  hasLowerTrack: true,
  min: 0,
  max: 100,
  step: 1
};

var _excluded$a = ["minRows", "maxRows", "style", "onChange", "onSelect"];
var parseStyleValue = function parseStyleValue(value) {
  return parseInt(value, 10) || 0;
};
var Textarea = React__default.forwardRef(function (_ref, ref) {
  var minRows = _ref.minRows,
      maxRows = _ref.maxRows,
      style = _ref.style,
      onChange = _ref.onChange,
      onSelect = _ref.onSelect,
      props = _objectWithoutProperties(_ref, _excluded$a);
  var fieldContext = useFieldContext();
  var textAreaRef = useRef();
  var shadowTextAreaRef = useRef(null);
  var _useState = useState({
    overflow: false,
    height: 0
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];
  var isControlled = props.value !== null && props.value !== undefined;
  var isAutoResizable = (minRows !== undefined || maxRows !== undefined) && !props.isResizable;
  var calculateHeight = useCallback(function () {
    if (!isAutoResizable) {
      return;
    }
    var textarea = textAreaRef.current;
    var computedStyle = window.getComputedStyle(textarea);
    var shadowTextArea = shadowTextAreaRef.current;
    shadowTextArea.style.width = computedStyle.width;
    shadowTextArea.value = textarea.value || textarea.placeholder || ' ';
    var boxSizing = computedStyle.boxSizing;
    var padding = parseStyleValue(computedStyle.paddingBottom) + parseStyleValue(computedStyle.paddingTop);
    var border = parseStyleValue(computedStyle.borderTopWidth) + parseStyleValue(computedStyle.borderBottomWidth);
    var innerHeight = shadowTextArea.scrollHeight - padding;
    shadowTextArea.value = 'x';
    var singleRowHeight = shadowTextArea.scrollHeight - padding;
    var outerHeight = innerHeight;
    if (minRows) {
      outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
    }
    if (maxRows) {
      outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
    }
    outerHeight = Math.max(outerHeight, singleRowHeight);
    var updatedHeight = outerHeight + (boxSizing === 'border-box' ? padding + border : 0);
    var overflow = Math.abs(outerHeight - innerHeight) <= 1;
    setState(function (prevState) {
      if (updatedHeight > 0 && Math.abs((prevState.height || 0) - updatedHeight) > 1 || prevState.overflow !== overflow) {
        return {
          overflow: overflow,
          height: updatedHeight
        };
      }
      return prevState;
    });
  }, [maxRows, minRows, textAreaRef, isAutoResizable]);
  var onChangeHandler = useCallback(function (e) {
    if (!isControlled) {
      calculateHeight();
    }
    if (onChange) {
      onChange(e);
    }
  }, [calculateHeight, isControlled, onChange]);
  useEffect(function () {
    if (!isAutoResizable) {
      return undefined;
    }
    var resizeHandler = debounce(function () {
      calculateHeight();
    });
    window.addEventListener('resize', resizeHandler);
    return function () {
      resizeHandler.cancel();
      window.removeEventListener('resize', resizeHandler);
    };
  }, [calculateHeight, isAutoResizable]);
  useLayoutEffect(function () {
    calculateHeight();
  });
  var computedStyle = {};
  if (isAutoResizable) {
    computedStyle.height = state.height;
    computedStyle.overflow = state.overflow ? 'hidden' : undefined;
  }
  var onSelectHandler = props.readOnly ? composeEventHandlers(onSelect, function (event) {
    event.currentTarget.select();
  }) : onSelect;
  var combinedProps = _objectSpread2({
    ref: mergeRefs([textAreaRef, ref]),
    rows: minRows,
    onChange: onChangeHandler,
    onSelect: onSelectHandler,
    style: _objectSpread2(_objectSpread2({}, computedStyle), style)
  }, props);
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps, {
      isDescribed: true
    });
  }
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(StyledTextarea, combinedProps), isAutoResizable && React__default.createElement(StyledTextarea, {
    "aria-hidden": true,
    readOnly: true,
    isHidden: true,
    className: props.className,
    ref: shadowTextAreaRef,
    tabIndex: -1,
    isBare: props.isBare,
    isCompact: props.isCompact,
    style: style
  }));
});
Textarea.propTypes = {
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  focusInset: PropTypes.bool,
  isResizable: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var _excluded$9 = ["children"];
var Toggle = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$9);
  var fieldsetContext = useFieldsetContext();
  var fieldContext = useFieldContext();
  var combinedProps = _objectSpread2(_objectSpread2({
    ref: ref
  }, props), fieldsetContext);
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps);
  }
  return React__default.createElement(InputContext.Provider, {
    value: "toggle"
  }, React__default.createElement(StyledToggleInput, combinedProps), children);
});
Toggle.displayName = 'Toggle';

function _extends$l() { _extends$l = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$l.apply(this, arguments); }

var _ref$l = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M12.688 5.61a.5.5 0 01.69.718l-.066.062-5 4a.5.5 0 01-.542.054l-.082-.054-5-4a.5.5 0 01.55-.83l.074.05L8 9.359l4.688-3.75z"
});

function SvgChevronDownStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$l);
}

var _excluded$8 = ["onFocus", "onBlur", "disabled", "readOnly", "isFocused"];
var StartIcon = function StartIcon(props) {
  return React__default.createElement(StyledTextMediaFigure, _extends$t({
    position: "start"
  }, props));
};
var EndIcon = function EndIcon(props) {
  return React__default.createElement(StyledTextMediaFigure, _extends$t({
    position: "end"
  }, props));
};
var FauxInput = forwardRef(function (_ref, ref) {
  var onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      disabled = _ref.disabled,
      readOnly = _ref.readOnly,
      controlledIsFocused = _ref.isFocused,
      props = _objectWithoutProperties(_ref, _excluded$8);
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];
  var onFocusHandler = composeEventHandlers(onFocus, function () {
    setIsFocused(true);
  });
  var onBlurHandler = composeEventHandlers(onBlur, function () {
    setIsFocused(false);
  });
  return React__default.createElement(StyledTextFauxInput, _extends$t({
    onFocus: onFocusHandler,
    onBlur: onBlurHandler,
    isFocused: controlledIsFocused === undefined ? isFocused : controlledIsFocused,
    isReadOnly: readOnly,
    isDisabled: disabled,
    tabIndex: disabled ? undefined : 0
  }, props, {
    ref: ref
  }));
});
FauxInput.StartIcon = StartIcon;
FauxInput.EndIcon = EndIcon;
FauxInput.displayName = 'FauxInput';
FauxInput.propTypes = {
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  focusInset: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var _excluded$7 = ["disabled", "isCompact"];
var Select = React__default.forwardRef(function (_ref, ref) {
  var disabled = _ref.disabled,
      isCompact = _ref.isCompact,
      props = _objectWithoutProperties(_ref, _excluded$7);
  var fieldContext = useFieldContext();
  var combinedProps = _objectSpread2({
    disabled: disabled,
    isCompact: isCompact,
    ref: ref
  }, props);
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps, {
      isDescribed: true
    });
  }
  return React__default.createElement(StyledSelectWrapper, {
    isCompact: isCompact
  }, React__default.createElement(StyledSelect, combinedProps), !props.isBare && React__default.createElement(FauxInput.EndIcon, {
    isDisabled: disabled
  }, React__default.createElement(SvgChevronDownStroke, null)));
});
Select.propTypes = {
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  focusInset: PropTypes.bool,
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var _excluded$6 = ["min", "max", "minValue", "maxValue", "disabled", "step", "onChange", "theme", "onMouseDown"];
var MultiThumbRange = function MultiThumbRange(_ref) {
  var min = _ref.min,
      max = _ref.max,
      minValue = _ref.minValue,
      maxValue = _ref.maxValue,
      disabled = _ref.disabled,
      step = _ref.step,
      onChange = _ref.onChange,
      theme = _ref.theme,
      onMouseDown = _ref.onMouseDown,
      props = _objectWithoutProperties(_ref, _excluded$6);
  var themedDocument = useDocument(theme);
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMinThumbFocused = _useState2[0],
      setIsMinThumbFocused = _useState2[1];
  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      railWidth = _useState4[0],
      setRailWidth = _useState4[1];
  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isMousedDown = _useState6[0],
      setIsMousedDown = _useState6[1];
  var trackRailRef = useRef(null);
  var minThumbRef = useRef(null);
  var maxThumbRef = useRef(null);
  var fieldContext = useFieldContext();
  var _ref2 = fieldContext || {},
      isLabelHovered = _ref2.isLabelHovered,
      isLabelActive = _ref2.isLabelActive,
      multiThumbRangeRef = _ref2.multiThumbRangeRef;
  var onWindowResize = useCallback(debounce(function () {
    if (trackRailRef.current) {
      setRailWidth(trackRailRef.current.getBoundingClientRect().width);
    }
  }, 100), []);
  useEffect(function () {
    if (multiThumbRangeRef) {
      multiThumbRangeRef.current = minThumbRef.current;
    }
  }, [multiThumbRangeRef]);
  useEffect(function () {
    onWindowResize();
    window.addEventListener('resize', onWindowResize);
    return function () {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [onWindowResize]);
  var calculateMinPosition = useCallback(function (minDistance) {
    var boundedMinValue = minValue;
    if (boundedMinValue < min) {
      boundedMinValue = min;
    } else if (boundedMinValue > maxValue) {
      boundedMinValue = maxValue;
    } else if (boundedMinValue > max) {
      boundedMinValue = max;
    }
    return (boundedMinValue - min) / (max - min) * (railWidth - minDistance);
  }, [max, maxValue, min, minValue, railWidth]);
  var calculateMaxPosition = useCallback(function (minDistance) {
    var boundedMaxValue = maxValue;
    if (boundedMaxValue > max) {
      boundedMaxValue = max;
    } else if (boundedMaxValue < minValue) {
      boundedMaxValue = minValue;
    } else if (boundedMaxValue < min) {
      boundedMaxValue = min;
    }
    return (boundedMaxValue - min) / (max - min) * (railWidth - minDistance) + minDistance;
  }, [max, maxValue, min, minValue, railWidth]);
  var onRangeValuesChange = useCallback(function () {
    var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        newMinValue = _ref3.minValue,
        newMaxValue = _ref3.maxValue;
    if (disabled) {
      return;
    }
    if (onChange && (newMinValue !== minValue || newMaxValue !== maxValue)) {
      onChange({
        minValue: newMinValue,
        maxValue: newMaxValue
      });
    }
  }, [disabled, maxValue, minValue, onChange]);
  var updateMinThumbSlider = useCallback(function (value) {
    var managedValue = value;
    if (value < min) {
      managedValue = min;
    } else if (value > maxValue) {
      managedValue = maxValue;
    }
    onRangeValuesChange({
      maxValue: maxValue,
      minValue: managedValue
    });
  }, [maxValue, min, onRangeValuesChange]);
  var updateMaxThumbSlider = useCallback(function (value) {
    var managedValue = value;
    if (value < minValue) {
      managedValue = minValue;
    } else if (value > max) {
      managedValue = max;
    }
    onRangeValuesChange({
      maxValue: managedValue,
      minValue: minValue
    });
  }, [max, minValue, onRangeValuesChange]);
  var calculateUpdatedValue = useCallback(function (e) {
    if (!trackRailRef.current) {
      return undefined;
    }
    var trackOffsetLeft = trackRailRef.current.getBoundingClientRect().left;
    var trackOffsetRight = trackOffsetLeft + trackRailRef.current.getBoundingClientRect().width;
    var trackWidth = trackRailRef.current.getBoundingClientRect().width;
    var diffX = e.pageX - (theme.rtl ? trackOffsetRight : trackOffsetLeft);
    if (theme.rtl) {
      diffX *= -1;
    }
    var newValue = min + parseInt((max - min) * diffX / trackWidth, 10);
    return Math.floor(newValue / step) * step;
  }, [max, min, step, theme.rtl]);
  var onDocumentMouseMove = useCallback(function (e) {
    var newValue = calculateUpdatedValue(e);
    if (isMinThumbFocused) {
      updateMinThumbSlider(newValue);
    } else {
      updateMaxThumbSlider(newValue);
    }
  }, [calculateUpdatedValue, isMinThumbFocused, updateMinThumbSlider, updateMaxThumbSlider]);
  var removeDragEvents = useCallback(function () {
    if (themedDocument) {
      themedDocument.removeEventListener('mousemove', onDocumentMouseMove);
      themedDocument.removeEventListener('mouseup', removeDragEvents);
    }
    setIsMousedDown(false);
  }, [onDocumentMouseMove, themedDocument]);
  var onTrackMouseDown = useCallback(function (e) {
    if (e.button !== 0 || disabled) {
      return;
    }
    e.preventDefault();
    var valueAtMouseDown = calculateUpdatedValue(e);
    if (valueAtMouseDown === undefined || minValue === undefined || maxValue === undefined) {
      return;
    }
    var distanceFromMinThumb = Math.abs(minValue - valueAtMouseDown);
    var distanceFromMaxThumb = Math.abs(maxValue - valueAtMouseDown);
    if (distanceFromMinThumb <= distanceFromMaxThumb) {
      minThumbRef.current && minThumbRef.current.focus();
      updateMinThumbSlider(valueAtMouseDown);
    } else {
      maxThumbRef.current && maxThumbRef.current.focus();
      updateMaxThumbSlider(valueAtMouseDown);
    }
  }, [calculateUpdatedValue, disabled, maxValue, minValue, updateMaxThumbSlider, updateMinThumbSlider]);
  useEffect(function () {
    if (isMousedDown && themedDocument) {
      themedDocument.addEventListener('mousemove', onDocumentMouseMove);
      themedDocument.addEventListener('mouseup', removeDragEvents);
    }
    return function () {
      if (isMousedDown && themedDocument) {
        themedDocument.removeEventListener('mousemove', onDocumentMouseMove);
        themedDocument.removeEventListener('mouseup', removeDragEvents);
      }
    };
  }, [isMousedDown, onDocumentMouseMove, removeDragEvents, themedDocument]);
  var _onKeyDown = function onKeyDown(type) {
    return function (e) {
      var isMinThumb = type === 'min';
      var keyIntercepted = false;
      var decrementThumb = function decrementThumb() {
        if (isMinThumb) {
          updateMinThumbSlider(minValue - step);
        } else {
          updateMaxThumbSlider(maxValue - step);
        }
      };
      var incrementThumb = function incrementThumb() {
        if (isMinThumb) {
          updateMinThumbSlider(minValue + step);
        } else {
          updateMaxThumbSlider(maxValue + step);
        }
      };
      switch (e.keyCode) {
        case KEY_CODES.LEFT:
          if (theme.rtl) {
            incrementThumb();
          } else {
            decrementThumb();
          }
          keyIntercepted = true;
          break;
        case KEY_CODES.DOWN:
          decrementThumb();
          keyIntercepted = true;
          break;
        case KEY_CODES.RIGHT:
          if (theme.rtl) {
            decrementThumb();
          } else {
            incrementThumb();
          }
          keyIntercepted = true;
          break;
        case KEY_CODES.UP:
          incrementThumb();
          keyIntercepted = true;
          break;
        case KEY_CODES.HOME:
          if (isMinThumb) {
            updateMinThumbSlider(min);
          } else {
            updateMaxThumbSlider(min);
          }
          keyIntercepted = true;
          break;
        case KEY_CODES.END:
          if (isMinThumb) {
            updateMinThumbSlider(max);
          } else {
            updateMaxThumbSlider(max);
          }
          keyIntercepted = true;
          break;
      }
      if (keyIntercepted) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
  };
  var MIN_DISTANCE = 0;
  var minPosition = calculateMinPosition(MIN_DISTANCE);
  var maxPosition = calculateMaxPosition(MIN_DISTANCE);
  var sliderBackgroundSize = Math.abs(maxPosition) - Math.abs(minPosition);
  var onSliderMouseDown = composeEventHandlers(onMouseDown, onTrackMouseDown);
  return React__default.createElement(StyledSlider, _extends$t({
    isDisabled: disabled,
    onMouseDown: onSliderMouseDown
  }, props), React__default.createElement(StyledSliderTrack, {
    backgroundSize: sliderBackgroundSize,
    backgroundPosition: theme.rtl ? railWidth - maxPosition : minPosition,
    isDisabled: disabled
  }, React__default.createElement(StyledSliderTrackRail, {
    ref: trackRailRef
  }, React__default.createElement(StyledSliderThumb, {
    role: "slider",
    tabIndex: disabled ? undefined : 0,
    "aria-valuemin": min,
    "aria-valuemax": maxValue,
    "aria-valuenow": minValue,
    "aria-valuetext": minValue,
    isDisabled: disabled,
    position: minPosition,
    ref: minThumbRef,
    onKeyDown: function onKeyDown(e) {
      return _onKeyDown('min')(e);
    },
    onFocus: function onFocus() {
      setIsMinThumbFocused(true);
    },
    onBlur: function onBlur() {
      setIsMinThumbFocused(false);
    },
    onMouseDown: function onMouseDown(e) {
      setIsMousedDown(true);
      e.preventDefault();
      e.stopPropagation();
      minThumbRef.current && minThumbRef.current.focus();
    },
    "data-garden-active": isLabelActive,
    "data-garden-hover": isLabelHovered
  }), React__default.createElement(StyledSliderThumb, {
    role: "slider",
    tabIndex: disabled ? undefined : 0,
    "aria-valuemin": minValue,
    "aria-valuemax": max,
    "aria-valuenow": maxValue,
    "aria-valuetext": maxValue,
    isDisabled: disabled,
    position: maxPosition,
    onKeyDown: function onKeyDown(e) {
      return _onKeyDown('max')(e);
    },
    ref: maxThumbRef,
    onMouseDown: function onMouseDown(e) {
      setIsMousedDown(true);
      e.preventDefault();
      e.stopPropagation();
      maxThumbRef.current && maxThumbRef.current.focus();
    }
  }))));
};
MultiThumbRange.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  step: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func
};
MultiThumbRange.defaultProps = {
  min: 0,
  max: 100,
  minValue: 0,
  maxValue: 100,
  step: 1,
  theme: DEFAULT_THEME
};
var MultiThumbRange$1 = withTheme(MultiThumbRange);

var TilesContext = createContext(undefined);
var useTilesContext = function useTilesContext() {
  return useContext(TilesContext);
};

var _excluded$5 = ["children", "value", "disabled"];
var Tile = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      value = _ref.value,
      disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, _excluded$5);
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];
  var tilesContext = useTilesContext();
  var inputRef = useRef(null);
  var inputProps;
  if (tilesContext) {
    inputProps = {
      name: tilesContext.name,
      checked: tilesContext.value === value,
      onChange: tilesContext.onChange
    };
  }
  return React__default.createElement(StyledTile, _extends$t({
    ref: ref,
    "aria-disabled": disabled,
    isDisabled: disabled,
    isFocused: isFocused,
    isSelected: tilesContext && tilesContext.value === value
  }, props), children, React__default.createElement(StyledTileInput, _extends$t({}, inputProps, {
    disabled: disabled,
    value: value,
    onBlur: function onBlur() {
      return setIsFocused(false);
    },
    onFocus: function onFocus(e) {
      e.persist();
      setTimeout(function () {
        if (e.target.getAttribute('data-garden-focus-visible')) {
          setIsFocused(true);
        }
      }, 1);
    },
    type: "radio",
    ref: inputRef
  })));
});
Tile.displayName = 'Tile';
Tile.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool
};

var Description = React__default.forwardRef(function (props, ref) {
  var tilesContext = useTilesContext();
  return React__default.createElement(StyledTileDescription, _extends$t({
    ref: ref,
    isCentered: tilesContext && tilesContext.isCentered
  }, props));
});
Description.displayName = 'TileDescription';

var Icon = React__default.forwardRef(function (props, ref) {
  var tileContext = useTilesContext();
  return React__default.createElement(StyledTileIcon, _extends$t({
    ref: ref,
    isCentered: tileContext && tileContext.isCentered
  }, props));
});
Icon.displayName = 'TileIcon';

var Label = React__default.forwardRef(function (props, forwardedRef) {
  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      title = _useState2[0],
      setTitle = _useState2[1];
  var ref = useRef();
  var tilesContext = useTilesContext();
  useEffect(function () {
    if (ref.current) {
      setTitle(ref.current.textContent || undefined);
    }
  }, [ref]);
  return React__default.createElement(StyledTileLabel, _extends$t({
    ref: mergeRefs([ref, forwardedRef]),
    title: title,
    isCentered: tilesContext && tilesContext.isCentered
  }, props));
});
Label.displayName = 'TileLabel';

var _excluded$4 = ["onChange", "value", "name", "isCentered"];
var Tiles = React__default.forwardRef(function (_ref, ref) {
  var onChange = _ref.onChange,
      controlledValue = _ref.value,
      name = _ref.name,
      isCentered = _ref.isCentered,
      props = _objectWithoutProperties(_ref, _excluded$4);
  var _useState = useState(controlledValue),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];
  var handleOnChange = useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    setValue(args[0].target.value);
    if (onChange) {
      onChange.apply(void 0, args);
    }
  }, [onChange]);
  var selectedValue = getControlledValue(controlledValue, value);
  var tileContext = useMemo(function () {
    return {
      onChange: handleOnChange,
      value: selectedValue,
      name: name,
      isCentered: isCentered
    };
  }, [handleOnChange, selectedValue, name, isCentered]);
  return React__default.createElement(TilesContext.Provider, {
    value: tileContext
  }, React__default.createElement("div", _extends$t({
    ref: ref,
    role: "radiogroup"
  }, props)));
});
Tiles.displayName = 'Tiles';
Tiles.Tile = Tile;
Tiles.Icon = Icon;
Tiles.Label = Label;
Tiles.Description = Description;
Tiles.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  isCentered: PropTypes.bool
};
Tiles.defaultProps = {
  isCentered: true
};

var _excluded$3 = ["isCompact"];
var InputGroup = React__default.forwardRef(function (_ref, ref) {
  var isCompact = _ref.isCompact,
      props = _objectWithoutProperties(_ref, _excluded$3);
  var contextValue = useMemo(function () {
    return {
      isCompact: isCompact
    };
  }, [isCompact]);
  return React__default.createElement(InputGroupContext.Provider, {
    value: contextValue
  }, React__default.createElement(StyledInputGroup, _extends$t({
    ref: ref,
    isCompact: isCompact
  }, props)));
});
InputGroup.displayName = 'InputGroup';
InputGroup.propTypes = {
  isCompact: PropTypes.bool
};

var _excluded$2 = ["disabled"];
var FileUpload = React__default.forwardRef(function (_ref, ref) {
  var disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, _excluded$2);
  return React__default.createElement(StyledFileUpload, _extends$t({
    ref: ref,
    "aria-disabled": disabled
  }, props));
});
FileUpload.propTypes = {
  isDragging: PropTypes.bool,
  isCompact: PropTypes.bool,
  disabled: PropTypes.bool
};
FileUpload.displayName = 'FileUpload';

var Item = forwardRef(function (_ref, ref) {
  var props = _extends$t({}, _ref);
  return React__default.createElement(StyledFileListItem, _extends$t({}, props, {
    ref: ref
  }));
});
Item.displayName = 'FileList.Item';

var FileList = forwardRef(function (_ref, ref) {
  var props = _extends$t({}, _ref);
  return React__default.createElement(StyledFileList, _extends$t({}, props, {
    ref: ref
  }));
});
FileList.displayName = 'FileList';
FileList.Item = Item;

function _extends$k() { _extends$k = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$k.apply(this, arguments); }

var _ref$k = /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M3 9l6-6m0 6L3 3"
});

function SvgXStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$k);
}

function _extends$j() { _extends$j = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$j.apply(this, arguments); }

var _ref$j = /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M3 13L13 3m0 10L3 3"
});

function SvgXStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$j);
}

var FileContext = createContext(undefined);
var useFileContext = function useFileContext() {
  return useContext(FileContext);
};

var Close = React__default.forwardRef(function (props, ref) {
  var fileContext = useFileContext();
  var onMouseDown = composeEventHandlers(props.onMouseDown, function (event) {
    return event.preventDefault();
  }
  );
  return React__default.createElement(StyledFileClose, _extends$t({
    ref: ref
  }, props, {
    onMouseDown: onMouseDown
  }), fileContext && fileContext.isCompact ? React__default.createElement(SvgXStroke$1, null) : React__default.createElement(SvgXStroke, null));
});
Close.displayName = 'File.Close';

function _extends$i() { _extends$i = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$i.apply(this, arguments); }

var _ref$i = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M4.5 2.5V1c0-.3.2-.5.5-.5h2c.3 0 .5.2.5.5v1.5M2 2.5h8m-5.5 7V5m3 4.5V5m-5-.5V11c0 .3.2.5.5.5h6c.3 0 .5-.2.5-.5V4.5"
});

function SvgTrashStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$i);
}

function _extends$h() { _extends$h = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$h.apply(this, arguments); }

var _ref$h = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M6.5 2.5V1c0-.3.2-.5.5-.5h2c.3 0 .5.2.5.5v1.5M3 2.5h10m-6.5 11v-8m3 8v-8m-6-1V15c0 .3.2.5.5.5h8c.3 0 .5-.2.5-.5V4.5"
});

function SvgTrashStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$h);
}

var Delete = React__default.forwardRef(function (props, ref) {
  var fileContext = useFileContext();
  var onMouseDown = composeEventHandlers(props.onMouseDown, function (event) {
    return event.preventDefault();
  }
  );
  return React__default.createElement(StyledFileDelete, _extends$t({
    ref: ref
  }, props, {
    onMouseDown: onMouseDown
  }), fileContext && fileContext.isCompact ? React__default.createElement(SvgTrashStroke$1, null) : React__default.createElement(SvgTrashStroke, null));
});
Delete.displayName = 'File.Delete';

function _extends$g() { _extends$g = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }

var _ref$g = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M10.5 3.21V11a.5.5 0 01-.5.5H2a.5.5 0 01-.5-.5V1A.5.5 0 012 .5h5.79a.5.5 0 01.35.15l2.21 2.21a.5.5 0 01.15.35zM7.5.5V3a.5.5 0 00.5.5h2.5m-7 6h5"
});

var _ref2$3 = /*#__PURE__*/React.createElement("rect", {
  width: 6,
  height: 3,
  x: 3,
  y: 5,
  fill: "currentColor",
  rx: 0.5,
  ry: 0.5
});

function SvgFilePdfStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$g, _ref2$3);
}

function _extends$f() { _extends$f = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }

var _ref$f = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M4.5.5v8m0-6h1m-2 1h1m0 1h1m-2 1h1m0 1h1m-2 1h1m6-4.29V11c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h5.79c.13 0 .26.05.35.15l2.21 2.21c.1.09.15.21.15.35zM7.5.5V3c0 .28.22.5.5.5h2.5"
});

function SvgFileZipStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$f);
}

function _extends$e() { _extends$e = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }

var _ref$e = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  d: "M10.5 3.21V11c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h5.79c.13 0 .26.05.35.15l2.21 2.21c.1.09.15.21.15.35zM7.5.5V3c0 .28.22.5.5.5h2.5m-7 6L5 8l1.5 1.5 1-1 1 1"
});

var _ref2$2 = /*#__PURE__*/React.createElement("circle", {
  cx: 8,
  cy: 6,
  r: 1,
  fill: "currentColor"
});

function SvgFileImageStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$e, _ref2$2);
}

function _extends$d() { _extends$d = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }

var _ref$d = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M3.5 5.5h5m-5 2h5m-5 2h5m2-6.29V11c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h5.79c.13 0 .26.05.35.15l2.21 2.21c.1.09.15.21.15.35zM7.5.5V3c0 .28.22.5.5.5h2.5"
});

function SvgFileDocumentStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$d);
}

function _extends$c() { _extends$c = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }

var _ref$c = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M3.5 5.5h1m-1 2h1m-1 2h1m2-4h2m-2 2h2m-2 2h2m2-6.29V11c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h5.79c.13 0 .26.05.35.15l2.21 2.21c.1.09.15.21.15.35zM7.5.5V3c0 .28.22.5.5.5h2.5"
});

function SvgFileSpreadsheetStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$c);
}

function _extends$b() { _extends$b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }

var _ref$b = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  d: "M10.5 3.21V11c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h5.79c.13 0 .26.05.35.15l2.21 2.21c.1.09.15.21.15.35zM6 9.5h2c.28 0 .5-.22.5-.5V8c0-.28-.22-.5-.5-.5H6c-.28 0-.5.22-.5.5v1c0 .28.22.5.5.5zm-2-2h2c.28 0 .5-.22.5-.5V6c0-.28-.22-.5-.5-.5H4c-.28 0-.5.22-.5.5v1c0 .28.22.5.5.5zm3.5-7V3c0 .28.22.5.5.5h2.5"
});

function SvgFilePresentationStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$b);
}

function _extends$a() { _extends$a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }

var _ref$a = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  d: "M10.5 3.21V11c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h5.79c.13 0 .26.05.35.15l2.21 2.21c.1.09.15.21.15.35zM7.5.5V3c0 .28.22.5.5.5h2.5"
});

function SvgFileGenericStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$a);
}

function _extends$9() { _extends$9 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }

var _ref$9 = /*#__PURE__*/React.createElement("g", {
  fill: "none",
  stroke: "currentColor"
}, /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  d: "M3.5 6l2 2L9 4.5"
}), /*#__PURE__*/React.createElement("circle", {
  cx: 6,
  cy: 6,
  r: 5.5
}));

function SvgCheckCircleStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$9);
}

function _extends$8() { _extends$8 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }

var _ref$8 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M10.5 3.21V11c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h5.79c.13 0 .26.05.35.15l2.21 2.21c.1.09.15.21.15.35zM7.5.5V3c0 .28.22.5.5.5h2.5M4 9.5l4-4m0 4l-4-4"
});

function SvgFileErrorStroke$1(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$8);
}

function _extends$7() { _extends$7 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }

var _ref$7 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M14.5 4.2V15a.5.5 0 01-.5.5H2a.5.5 0 01-.5-.5V1A.5.5 0 012 .5h8.85a.5.5 0 01.36.15l3.15 3.2a.5.5 0 01.14.35zm-10 8.3h7m-7-2h7m-1-10V4a.5.5 0 00.5.5h3.5"
});

var _ref2$1 = /*#__PURE__*/React.createElement("rect", {
  width: 8,
  height: 2,
  x: 4,
  y: 7,
  fill: "currentColor",
  rx: 0.5,
  ry: 0.5
});

function SvgFilePdfStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$7, _ref2$1);
}

function _extends$6() { _extends$6 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }

var _ref$6 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M6.5.5v11M5 2.5h1.5m0 1H8m-3 1h1.5m0 1H8m-3 1h1.5m0 1H8m-3 1h1.5m0 1H8m-3 1h1.5m8-6.3V15c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h8.85c.13 0 .26.05.36.15l3.15 3.2c.09.1.14.22.14.35zm-4-3.7V4c0 .28.22.5.5.5h3.5"
});

function SvgFileZipStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$6);
}

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

var _ref$5 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M14.5 4.2V15c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h8.85c.13 0 .26.05.36.15l3.15 3.2c.09.1.14.22.14.35zm-4-3.7V4c0 .28.22.5.5.5h3.5m-11 9l2.65-2.65c.2-.2.51-.2.71 0l1.79 1.79c.2.2.51.2.71 0l.79-.79c.2-.2.51-.2.71 0l1.65 1.65"
});

var _ref2 = /*#__PURE__*/React.createElement("circle", {
  cx: 10.5,
  cy: 8.5,
  r: 1.5,
  fill: "currentColor"
});

function SvgFileImageStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$5, _ref2);
}

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

var _ref$4 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M4.5 7.5h7m-7 1.97h7m-7 2h7m3-7.27V15c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h8.85c.13 0 .26.05.36.15l3.15 3.2c.09.1.14.22.14.35zm-4-3.7V4c0 .28.22.5.5.5h3.5"
});

function SvgFileDocumentStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$4);
}

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var _ref$3 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M4.5 7.5h2m-2 2h2m-2 2h2m2-4h3m-3 2h3m-3 2h3m3-7.3V15c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h8.85c.13 0 .26.05.36.15l3.15 3.2c.09.1.14.22.14.35zm-4-3.7V4c0 .28.22.5.5.5h3.5"
});

function SvgFileSpreadsheetStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$3);
}

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var _ref$2 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  d: "M14.5 4.2V15c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h8.85c.13 0 .26.05.36.15l3.15 3.2c.09.1.14.22.14.35zm-4-3.7V4c0 .28.22.5.5.5h3.5M7 9.5h4c.28 0 .5.22.5.5v3c0 .28-.22.5-.5.5H7c-.28 0-.5-.22-.5-.5v-3c0-.28.22-.5.5-.5zm-.5 2H5c-.28 0-.5-.22-.5-.5V8c0-.28.22-.5.5-.5h4c.28 0 .5.22.5.5v1.5"
});

function SvgFilePresentationStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$2);
}

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var _ref$1 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  d: "M14.5 4.2V15c0 .28-.22.5-.5.5H2c-.28 0-.5-.22-.5-.5V1c0-.28.22-.5.5-.5h8.85c.13 0 .26.05.36.15l3.15 3.2c.09.1.14.22.14.35zm-4-3.7V4c0 .28.22.5.5.5h3.5"
});

function SvgFileGenericStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$1);
}

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var _ref = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M14.5 4.205V15a.5.5 0 01-.5.5H2a.5.5 0 01-.5-.5V1A.5.5 0 012 .5h8.853a.5.5 0 01.356.15l3.148 3.204a.5.5 0 01.143.35zM10.5.5V4a.5.5 0 00.5.5h3.5m-9 8l5-5m0 5l-5-5"
});

function SvgFileErrorStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref);
}

var FileType;
(function (FileType) {
  FileType["pdf"] = "pdf";
  FileType["zip"] = "zip";
  FileType["image"] = "image";
  FileType["document"] = "document";
  FileType["spreadsheet"] = "spreadsheet";
  FileType["presentation"] = "presentation";
  FileType["generic"] = "generic";
})(FileType || (FileType = {}));
var ValidationType;
(function (ValidationType) {
  ValidationType["success"] = "success";
  ValidationType["error"] = "error";
})(ValidationType || (ValidationType = {}));
var ARRAY_FILE_TYPE = _toConsumableArray(Object.values(FileType));
var fileIconsDefault = {
  pdf: React__default.createElement(SvgFilePdfStroke, null),
  zip: React__default.createElement(SvgFileZipStroke, null),
  image: React__default.createElement(SvgFileImageStroke, null),
  document: React__default.createElement(SvgFileDocumentStroke, null),
  spreadsheet: React__default.createElement(SvgFileSpreadsheetStroke, null),
  presentation: React__default.createElement(SvgFilePresentationStroke, null),
  generic: React__default.createElement(SvgFileGenericStroke, null),
  success: React__default.createElement(SvgCheckCircleStroke$1, null),
  error: React__default.createElement(SvgFileErrorStroke, null)
};
var fileIconsCompact = {
  pdf: React__default.createElement(SvgFilePdfStroke$1, null),
  zip: React__default.createElement(SvgFileZipStroke$1, null),
  image: React__default.createElement(SvgFileImageStroke$1, null),
  document: React__default.createElement(SvgFileDocumentStroke$1, null),
  spreadsheet: React__default.createElement(SvgFileSpreadsheetStroke$1, null),
  presentation: React__default.createElement(SvgFilePresentationStroke$1, null),
  generic: React__default.createElement(SvgFileGenericStroke$1, null),
  success: React__default.createElement(SvgCheckCircleStroke, null),
  error: React__default.createElement(SvgFileErrorStroke$1, null)
};

var _excluded$1 = ["children", "type", "isCompact", "focusInset", "validation"];
var File = forwardRef(function (_ref, ref) {
  var children = _ref.children,
      type = _ref.type,
      isCompact = _ref.isCompact,
      focusInset = _ref.focusInset,
      validation = _ref.validation,
      props = _objectWithoutProperties(_ref, _excluded$1);
  var fileContextValue = useMemo(function () {
    return {
      isCompact: isCompact
    };
  }, [isCompact]);
  var validationType = validation || type;
  return React__default.createElement(FileContext.Provider, {
    value: fileContextValue
  }, React__default.createElement(StyledFile, _extends$t({}, props, {
    isCompact: isCompact,
    focusInset: focusInset,
    validation: validation,
    ref: ref
  }), validationType && React__default.createElement(StyledFileIcon, {
    isCompact: isCompact
  }, isCompact ? fileIconsCompact[validationType] : fileIconsDefault[validationType]), Children.map(children, function (child) {
    return typeof child === 'string' ? React__default.createElement("span", null, child) : child;
  })));
});
File.displayName = 'File';
File.Close = Close;
File.Delete = Delete;
File.propTypes = {
  focusInset: PropTypes.bool,
  isCompact: PropTypes.bool,
  type: PropTypes.oneOf(ARRAY_FILE_TYPE),
  validation: PropTypes.oneOf(['success', 'error'])
};

var _excluded = ["start", "end", "disabled", "isCompact", "isBare", "focusInset", "readOnly", "validation", "wrapperProps", "wrapperRef", "onSelect"],
    _excluded2 = ["onClick", "onFocus", "onBlur", "onMouseOver", "onMouseOut"];
var MediaInput = React__default.forwardRef(function (_ref, ref) {
  var start = _ref.start,
      end = _ref.end,
      disabled = _ref.disabled,
      isCompact = _ref.isCompact,
      isBare = _ref.isBare,
      focusInset = _ref.focusInset,
      readOnly = _ref.readOnly,
      validation = _ref.validation,
      _ref$wrapperProps = _ref.wrapperProps,
      wrapperProps = _ref$wrapperProps === void 0 ? {} : _ref$wrapperProps,
      wrapperRef = _ref.wrapperRef,
      onSelect = _ref.onSelect,
      props = _objectWithoutProperties(_ref, _excluded);
  var fieldContext = useFieldContext();
  var inputRef = useRef();
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];
  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isHovered = _useState4[0],
      setIsHovered = _useState4[1];
  var onClick = wrapperProps.onClick,
      onFocus = wrapperProps.onFocus,
      onBlur = wrapperProps.onBlur,
      onMouseOver = wrapperProps.onMouseOver,
      onMouseOut = wrapperProps.onMouseOut,
      otherWrapperProps = _objectWithoutProperties(wrapperProps, _excluded2);
  var onFauxInputClickHandler = composeEventHandlers(onClick, function () {
    inputRef.current && inputRef.current.focus();
  });
  var onFauxInputFocusHandler = composeEventHandlers(onFocus, function () {
    setIsFocused(true);
  });
  var onFauxInputBlurHandler = composeEventHandlers(onBlur, function () {
    setIsFocused(false);
  });
  var onFauxInputMouseOverHandler = composeEventHandlers(onMouseOver, function () {
    setIsHovered(true);
  });
  var onFauxInputMouseOutHandler = composeEventHandlers(onMouseOut, function () {
    setIsHovered(false);
  });
  var onSelectHandler = readOnly ? composeEventHandlers(onSelect, function (event) {
    event.currentTarget.select();
  }) : onSelect;
  var combinedProps = _objectSpread2({
    disabled: disabled,
    readOnly: readOnly,
    ref: mergeRefs([inputRef, ref]),
    onSelect: onSelectHandler
  }, props);
  var isLabelHovered;
  if (fieldContext) {
    combinedProps = fieldContext.getInputProps(combinedProps, {
      isDescribed: true
    });
    isLabelHovered = fieldContext.isLabelHovered;
  }
  return React__default.createElement(FauxInput, _extends$t({
    tabIndex: null,
    onClick: onFauxInputClickHandler,
    onFocus: onFauxInputFocusHandler,
    onBlur: onFauxInputBlurHandler,
    onMouseOver: onFauxInputMouseOverHandler,
    onMouseOut: onFauxInputMouseOutHandler,
    disabled: disabled,
    isFocused: isFocused,
    isHovered: isHovered || isLabelHovered,
    isCompact: isCompact,
    isBare: isBare,
    focusInset: focusInset,
    readOnly: readOnly,
    validation: validation,
    mediaLayout: true
  }, otherWrapperProps, {
    ref: wrapperRef
  }), start && React__default.createElement(FauxInput.StartIcon, {
    isDisabled: disabled,
    isFocused: isFocused,
    isHovered: isHovered || isLabelHovered
  }, start), React__default.createElement(StyledTextMediaInput, combinedProps), end && React__default.createElement(FauxInput.EndIcon, {
    isDisabled: disabled,
    isFocused: isFocused,
    isHovered: isHovered || isLabelHovered
  }, end));
});
MediaInput.propTypes = {
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  focusInset: PropTypes.bool,
  start: PropTypes.node,
  end: PropTypes.node,
  validation: PropTypes.oneOf(['success', 'warning', 'error']),
  wrapperProps: PropTypes.object
};

export { Checkbox, FauxInput, Field, Fieldset, File, FileList, FileUpload, Hint, Input, InputGroup, Label$1 as Label, MediaInput, Message, MultiThumbRange$1 as MultiThumbRange, Radio, Range, Select, Textarea, Tiles, Toggle };
